/**
 * 🚀 GPU-ACCELERATED ÖLÇÜM MOTORU
 * Native CAD'den daha hızlı ölçüm sistemi
 *
 * Performans Hedefleri:
 * - Raycasting: 5-10x hızlandırma (BVH)
 * - Geometri Hesaplamaları: 10-50x hızlandırma (WebGPU Compute)
 * - CPU Kullanımı: %80 azaltma
 * - Frame Rate: 60 FPS @ 1M+ vertices
 */

import * as THREE from 'three';
import { SAH } from 'three-mesh-bvh';

/**
 * GPU Compute Shader Destekleme Kontrolü
 */
class GPUCapabilityDetector {
  private static _webGPUAvailable: boolean | null = null;
  private static _webGL2Available: boolean | null = null;

  static async checkWebGPU(): Promise<boolean> {
    if (this._webGPUAvailable !== null) return this._webGPUAvailable;

    try {
      if (!navigator.gpu) {
        this._webGPUAvailable = false;
        return false;
      }

      const adapter = await navigator.gpu.requestAdapter();
      if (!adapter) {
        this._webGPUAvailable = false;
        return false;
      }

      this._webGPUAvailable = true;
      return true;
    } catch (e) {
      this._webGPUAvailable = false;
      return false;
    }
  }

  static checkWebGL2(): boolean {
    if (this._webGL2Available !== null) return this._webGL2Available;

    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      this._webGL2Available = gl !== null;
      return this._webGL2Available;
    } catch (e) {
      this._webGL2Available = false;
      return false;
    }
  }

  static getBestAccelerationMode(): 'webgpu' | 'webgl2' | 'wasm' | 'cpu' {
    if (this._webGPUAvailable) return 'webgpu';
    if (this._webGL2Available) return 'webgl2';
    return 'wasm'; // WASM SIMD fallback
  }
}

/**
 * 🎯 KATMAN 1: BVH-ACCELERATED RAYCASTING (5-10x hızlandırma)
 * Three.js mesh raycasting'i GPU-friendly BVH ile hızlandır
 */
export class BVHRaycastOptimizer {
  private static initialized = false;
  private bvhCache: WeakMap<THREE.BufferGeometry, any> = new WeakMap();

  constructor() {
    if (!BVHRaycastOptimizer.initialized) {
      this.initializeBVH();
      BVHRaycastOptimizer.initialized = true;
    }
  }

  /**
   * BVH raycasting'i THREE.js'e entegre et
   */
  private initializeBVH(): void {
    // BVH prototype patching is handled centrally by FikoRaycastBVH.patchPrototypes()
    // No-op: prototypes are already patched at initialization time.
  }

  /**
   * Geometry için BVH tree oluştur (bir kez)
   * @param geometry - BufferGeometry
   */
  optimizeGeometry(geometry: THREE.BufferGeometry): void {
    if (this.bvhCache.has(geometry)) return; // Zaten optimize edilmiş

    const startTime = performance.now();

    // BVH tree hesapla
    (geometry as any).computeBoundsTree?.({
      maxLeafTris: 10, // Leaf node'da maksimum triangle sayısı (optimum: 10)
      strategy: SAH, // SAH (Surface Area Heuristic) - en iyi performans
      verbose: false
    });

    const elapsed = performance.now() - startTime;
    this.bvhCache.set(geometry, true);
  }

  /**
   * Mesh'i optimize et (BVH + diğer)
   */
  optimizeMesh(mesh: THREE.Mesh): void {
    if (!mesh.geometry) return;

    this.optimizeGeometry(mesh.geometry);

    // Frustum culling optimize et
    mesh.frustumCulled = true;

    // Render order optimize et (opaque objeler önce)
    if ((mesh.material as any).transparent) {
      mesh.renderOrder = 1000;
    }
  }

  /**
   * Scene'deki tüm mesh'leri optimize et
   */
  optimizeScene(scene: THREE.Scene | THREE.Object3D): void {
    scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        this.optimizeMesh(object);
      }
    });
  }

  /**
   * Geometry'yi dispose et
   */
  disposeGeometry(geometry: THREE.BufferGeometry): void {
    (geometry as any).disposeBoundsTree?.();
    this.bvhCache.delete(geometry);
  }
}

/**
 * 🎯 KATMAN 2: WebGPU COMPUTE SHADER (10-50x hızlandırma)
 * Mesafe, açı ve geometri hesaplamalarını GPU'da paralel çalıştır
 *
 * 🚀 PERFORMANS OPTİMİZASYONLARI:
 * - Buffer pooling: Her çağrıda yeni buffer oluşturmak yerine havuzdan al
 * - Pipeline caching: Shader'ları bir kez derle, sonra cache'den kullan
 * - Async scheduling: Multiple dispatch'leri birleştir
 */
export class WebGPUComputeEngine {
  private device: GPUDevice | null = null;
  private adapter: GPUAdapter | null = null;
  private available = false;

  // 🚀 PIPELINE CACHE - Shader'ları bir kez derle
  private pipelineCache: Map<string, GPUComputePipeline> = new Map();
  private shaderModuleCache: Map<string, GPUShaderModule> = new Map();

  // 🚀 BUFFER POOL - Buffer'ları yeniden kullan
  private bufferPool: Map<number, GPUBuffer[]> = new Map();
  private readonly MAX_POOL_SIZE = 10;

  // 🚀 BIND GROUP LAYOUT CACHE
  private bindGroupLayoutCache: Map<string, GPUBindGroupLayout> = new Map();

  async initialize(): Promise<boolean> {
    try {
      const hasWebGPU = await GPUCapabilityDetector.checkWebGPU();
      if (!hasWebGPU) {
        return false;
      }

      this.adapter = await navigator.gpu.requestAdapter();
      if (!this.adapter) return false;

      this.device = await this.adapter.requestDevice();
      this.available = true;

      // Pre-compile common shaders
      this.precompileCommonShaders();

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 🚀 Pre-compile common shaders for instant use
   */
  private async precompileCommonShaders(): Promise<void> {
    if (!this.device) return;

    // Pre-compile min distance shader
    const minDistShader = this.getMinDistanceShaderCode();
    this.getOrCreateShaderModule('minDist', minDistShader);

    // Pre-compile centroid shader
    const centroidShader = this.getCentroidShaderCode();
    this.getOrCreateShaderModule('centroid', centroidShader);
  }

  private getMinDistanceShaderCode(): string {
    return `
      struct MinDistResult {
        value: atomic<u32>,
      }

      @group(0) @binding(0) var<storage, read> positions1: array<vec3<f32>>;
      @group(0) @binding(1) var<storage, read> positions2: array<vec3<f32>>;
      @group(0) @binding(2) var<storage, read_write> result: MinDistResult;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) id: vec3<u32>) {
        let idx1 = id.x;
        let count1 = arrayLength(&positions1);

        if (idx1 >= count1) {
          return;
        }

        let p1 = positions1[idx1];
        var local_min: f32 = 999999.0;

        let count2 = arrayLength(&positions2);
        for (var idx2: u32 = 0u; idx2 < count2; idx2 = idx2 + 1u) {
          let p2 = positions2[idx2];
          let diff = p1 - p2;
          let dist = length(diff);
          local_min = min(local_min, dist);
        }

        let dist_as_uint = bitcast<u32>(local_min);
        atomicMin(&result.value, dist_as_uint);
      }
    `;
  }

  private getCentroidShaderCode(): string {
    return `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<storage, read_write> result: array<f32>;
      @group(0) @binding(2) var<uniform> vertexCount: u32;

      var<workgroup> localSums: array<vec4<f32>, 256>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
        let idx = gid.x;
        var sum = vec4<f32>(0.0, 0.0, 0.0, 0.0);

        if (idx < vertexCount) {
          let base = idx * 3u;
          sum = vec4<f32>(positions[base], positions[base + 1u], positions[base + 2u], 1.0);
        }

        localSums[lid.x] = sum;
        workgroupBarrier();

        for (var s = 128u; s > 0u; s = s >> 1u) {
          if (lid.x < s) {
            localSums[lid.x] = localSums[lid.x] + localSums[lid.x + s];
          }
          workgroupBarrier();
        }

        if (lid.x == 0u) {
          result[0] = result[0] + localSums[0].x;
          result[1] = result[1] + localSums[0].y;
          result[2] = result[2] + localSums[0].z;
          result[3] = result[3] + localSums[0].w;
        }
      }
    `;
  }

  /**
   * 🚀 Get or create cached shader module
   */
  private getOrCreateShaderModule(key: string, code: string): GPUShaderModule | null {
    if (!this.device) return null;

    if (this.shaderModuleCache.has(key)) {
      return this.shaderModuleCache.get(key)!;
    }

    const module = this.device.createShaderModule({ code });
    this.shaderModuleCache.set(key, module);
    return module;
  }

  /**
   * 🚀 Get buffer from pool or create new one
   */
  private getBufferFromPool(size: number, usage: number): GPUBuffer | null {
    if (!this.device) return null;

    // Round up to nearest power of 2 for better pooling
    const roundedSize = Math.pow(2, Math.ceil(Math.log2(Math.max(size, 16))));

    const pool = this.bufferPool.get(roundedSize);
    if (pool && pool.length > 0) {
      return pool.pop()!;
    }

    return this.device.createBuffer({
      size: roundedSize,
      usage: usage | GPUBufferUsage.COPY_DST,
    });
  }

  /**
   * 🚀 Return buffer to pool for reuse
   */
  private returnBufferToPool(buffer: GPUBuffer, size: number): void {
    const roundedSize = Math.pow(2, Math.ceil(Math.log2(Math.max(size, 16))));

    let pool = this.bufferPool.get(roundedSize);
    if (!pool) {
      pool = [];
      this.bufferPool.set(roundedSize, pool);
    }

    if (pool.length < this.MAX_POOL_SIZE) {
      pool.push(buffer);
    } else {
      buffer.destroy();
    }
  }

  /**
   * 🚀 Dispose all cached resources
   */
  dispose(): void {
    // Clear buffer pools
    for (const [, pool] of this.bufferPool) {
      for (const buffer of pool) {
        buffer.destroy();
      }
    }
    this.bufferPool.clear();

    // Clear caches
    this.pipelineCache.clear();
    this.shaderModuleCache.clear();
    this.bindGroupLayoutCache.clear();

    // Clear device reference (garbage collection will handle cleanup)
    // Note: GPUDevice.destroy() may not exist in all WebGPU implementations
    this.device = null;
    this.adapter = null;
    this.available = false;
  }

  /**
   * İki geometri arasındaki minimum mesafeyi GPU'da hesapla
   * @param positions1 - Float32Array vertex positions
   * @param positions2 - Float32Array vertex positions
   * @returns Minimum mesafe (mm)
   */
  async computeMinDistance(positions1: Float32Array, positions2: Float32Array): Promise<number> {
    if (!this.available || !this.device) {
      throw new Error('WebGPU not available');
    }

    const startTime = performance.now();

    // Compute shader - WGSL (WebGPU Shading Language)
    const shaderCode = `
      struct MinDistResult {
        value: atomic<u32>,  // f32'yi u32 olarak atomic kullanımı için
      }

      @group(0) @binding(0) var<storage, read> positions1: array<vec3<f32>>;
      @group(0) @binding(1) var<storage, read> positions2: array<vec3<f32>>;
      @group(0) @binding(2) var<storage, read_write> result: MinDistResult;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) id: vec3<u32>) {
        let idx1 = id.x;
        let count1 = arrayLength(&positions1);

        if (idx1 >= count1) {
          return;
        }

        let p1 = positions1[idx1];
        var local_min: f32 = 999999.0;

        // Her vertex için tüm vertex'lere mesafe hesapla
        let count2 = arrayLength(&positions2);
        for (var idx2: u32 = 0u; idx2 < count2; idx2 = idx2 + 1u) {
          let p2 = positions2[idx2];
          let diff = p1 - p2;
          let dist = length(diff);
          local_min = min(local_min, dist);
        }

        // Atomic min (u32 olarak)
        let dist_as_uint = bitcast<u32>(local_min);
        atomicMin(&result.value, dist_as_uint);
      }
    `;

    // Shader modülü oluştur
    const shaderModule = this.device.createShaderModule({
      code: shaderCode,
    });

    // Buffer'ları oluştur
    const positions1Buffer = this.device.createBuffer({
      size: positions1.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(positions1Buffer.getMappedRange()).set(positions1);
    positions1Buffer.unmap();

    const positions2Buffer = this.device.createBuffer({
      size: positions2.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(positions2Buffer.getMappedRange()).set(positions2);
    positions2Buffer.unmap();

    // Result buffer
    const resultBuffer = this.device.createBuffer({
      size: 4, // u32
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      mappedAtCreation: true,
    });
    new Uint32Array(resultBuffer.getMappedRange()).set([0x7F800000]); // f32 infinity as u32
    resultBuffer.unmap();

    // Staging buffer (GPU -> CPU okumak için)
    const stagingBuffer = this.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });

    // Bind group layout
    const bindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
      ],
    });

    const bindGroup = this.device.createBindGroup({
      layout: bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: positions1Buffer } },
        { binding: 1, resource: { buffer: positions2Buffer } },
        { binding: 2, resource: { buffer: resultBuffer } },
      ],
    });

    // Compute pipeline
    const pipeline = this.device.createComputePipeline({
      layout: this.device.createPipelineLayout({
        bindGroupLayouts: [bindGroupLayout],
      }),
      compute: {
        module: shaderModule,
        entryPoint: 'main',
      },
    });

    // Command encoder ve dispatch
    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(pipeline);
    passEncoder.setBindGroup(0, bindGroup);

    // Workgroup sayısı: vertex count / 256
    const workgroupCount = Math.ceil(positions1.length / 3 / 256);
    passEncoder.dispatchWorkgroups(workgroupCount);
    passEncoder.end();

    // Result'u staging buffer'a kopyala
    commandEncoder.copyBufferToBuffer(resultBuffer, 0, stagingBuffer, 0, 4);

    // Submit
    this.device.queue.submit([commandEncoder.finish()]);

    // Result'u oku
    await stagingBuffer.mapAsync(GPUMapMode.READ);
    const resultArray = new Uint32Array(stagingBuffer.getMappedRange());
    const minDistanceAsUint = resultArray[0];
    const minDistance = new Float32Array(new Uint32Array([minDistanceAsUint]).buffer)[0];
    stagingBuffer.unmap();

    // Cleanup
    positions1Buffer.destroy();
    positions2Buffer.destroy();
    resultBuffer.destroy();
    stagingBuffer.destroy();

    const elapsed = performance.now() - startTime;

    return minDistance;
  }

  /**
   * 🚀 Küre tespiti - GPU paralel hesaplama
   * Tüm noktaların merkeze olan uzaklıklarını paralel hesaplar
   * @param positions Float32Array [x1,y1,z1, x2,y2,z2, ...]
   * @returns {center, radius, quality} veya null
   */
  async detectSphere(positions: Float32Array): Promise<{ center: THREE.Vector3; radius: number; quality: number } | null> {
    if (!this.available || !this.device || positions.length < 12) { // En az 4 nokta
      return null;
    }

    const vertexCount = positions.length / 3;
    const startTime = performance.now();

    // 1. İlk adım: Centroid hesapla (GPU reduction)
    const centroidShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<storage, read_write> result: array<f32>; // [sumX, sumY, sumZ, count]
      @group(0) @binding(2) var<uniform> vertexCount: u32;

      var<workgroup> localSums: array<vec4<f32>, 256>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
        let idx = gid.x;
        var sum = vec4<f32>(0.0, 0.0, 0.0, 0.0);

        if (idx < vertexCount) {
          let base = idx * 3u;
          sum = vec4<f32>(positions[base], positions[base + 1u], positions[base + 2u], 1.0);
        }

        localSums[lid.x] = sum;
        workgroupBarrier();

        // Parallel reduction
        for (var s = 128u; s > 0u; s = s >> 1u) {
          if (lid.x < s) {
            localSums[lid.x] = localSums[lid.x] + localSums[lid.x + s];
          }
          workgroupBarrier();
        }

        if (lid.x == 0u) {
          // Atomic add to global result
          result[0] = result[0] + localSums[0].x;
          result[1] = result[1] + localSums[0].y;
          result[2] = result[2] + localSums[0].z;
          result[3] = result[3] + localSums[0].w;
        }
      }
    `;

    // 2. İkinci adım: Mesafe hesaplama ve istatistikler
    const distanceShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<uniform> center: vec3<f32>;
      @group(0) @binding(2) var<storage, read_write> distances: array<f32>;
      @group(0) @binding(3) var<uniform> vertexCount: u32;

      @compute @workgroup_size(64)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
        let idx = gid.x;
        if (idx >= vertexCount) {
          return;
        }

        let base = idx * 3u;
        let pos = vec3<f32>(positions[base], positions[base + 1u], positions[base + 2u]);
        let dist = length(pos - center);
        distances[idx] = dist;
      }
    `;

    try {
      // Centroid hesapla
      const posBuffer = this.device.createBuffer({
        size: positions.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(posBuffer.getMappedRange()).set(positions);
      posBuffer.unmap();

      const centroidResultBuffer = this.device.createBuffer({
        size: 16, // 4 floats
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const countBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Uint32Array(countBuffer.getMappedRange()).set([vertexCount]);
      countBuffer.unmap();

      // Centroid shader execute
      const centroidModule = this.device.createShaderModule({ code: centroidShader });
      const centroidBindGroupLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const centroidPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [centroidBindGroupLayout] }),
        compute: { module: centroidModule, entryPoint: 'main' },
      });
      const centroidBindGroup = this.device.createBindGroup({
        layout: centroidBindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centroidResultBuffer } },
          { binding: 2, resource: { buffer: countBuffer } },
        ],
      });

      let commandEncoder = this.device.createCommandEncoder();
      let passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(centroidPipeline);
      passEncoder.setBindGroup(0, centroidBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      passEncoder.end();

      const centroidStagingBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(centroidResultBuffer, 0, centroidStagingBuffer, 0, 16);
      this.device.queue.submit([commandEncoder.finish()]);

      await centroidStagingBuffer.mapAsync(GPUMapMode.READ);
      const centroidData = new Float32Array(centroidStagingBuffer.getMappedRange().slice(0));
      centroidStagingBuffer.unmap();

      const center = new THREE.Vector3(
        centroidData[0] / centroidData[3],
        centroidData[1] / centroidData[3],
        centroidData[2] / centroidData[3]
      );

      // Mesafeleri hesapla
      const centerBuffer = this.device.createBuffer({
        size: 16, // vec3 + padding
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Float32Array(centerBuffer.getMappedRange()).set([center.x, center.y, center.z, 0]);
      centerBuffer.unmap();

      const distancesBuffer = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const distModule = this.device.createShaderModule({ code: distanceShader });
      const distBindGroupLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const distPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [distBindGroupLayout] }),
        compute: { module: distModule, entryPoint: 'main' },
      });
      const distBindGroup = this.device.createBindGroup({
        layout: distBindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: distancesBuffer } },
          { binding: 3, resource: { buffer: countBuffer } },
        ],
      });

      commandEncoder = this.device.createCommandEncoder();
      passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(distPipeline);
      passEncoder.setBindGroup(0, distBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 64));
      passEncoder.end();

      const distStagingBuffer = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(distancesBuffer, 0, distStagingBuffer, 0, vertexCount * 4);
      this.device.queue.submit([commandEncoder.finish()]);

      await distStagingBuffer.mapAsync(GPUMapMode.READ);
      const distances = new Float32Array(distStagingBuffer.getMappedRange().slice(0));
      distStagingBuffer.unmap();

      // İstatistikleri CPU'da hesapla (küçük veri)
      let sum = 0, min = Infinity, max = -Infinity;
      for (let i = 0; i < distances.length; i++) {
        sum += distances[i];
        min = Math.min(min, distances[i]);
        max = Math.max(max, distances[i]);
      }
      const avgRadius = sum / distances.length;

      // Quality: tolerans içindeki noktaların oranı
      const tolerance = Math.max(0.1, avgRadius * 0.05);
      let inToleranceCount = 0;
      for (let i = 0; i < distances.length; i++) {
        if (Math.abs(distances[i] - avgRadius) <= tolerance) {
          inToleranceCount++;
        }
      }
      const quality = (inToleranceCount / distances.length) * 100;

      // Cleanup
      posBuffer.destroy();
      centroidResultBuffer.destroy();
      countBuffer.destroy();
      centerBuffer.destroy();
      distancesBuffer.destroy();
      centroidStagingBuffer.destroy();
      distStagingBuffer.destroy();

      const elapsed = performance.now() - startTime;

      // %85+ kalite ve max-min farkı küçükse küre
      if (quality > 85 && (max - min) < avgRadius * 0.3) {
        return { center, radius: avgRadius, quality };
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * 🚀 Çember tespiti - GPU paralel hesaplama (2D projection)
   * Noktaları düzleme yansıtıp çember fitting yapar
   */
  async detectCircle(positions: Float32Array): Promise<{ center: THREE.Vector3; radius: number; quality: number } | null> {
    // Küre tespiti ile aynı mantık - 3D'de çember = küre kesiti
    return this.detectSphere(positions);
  }

  /**
   * 🚀 Silindir tespiti - GPU paralel hesaplama
   * PCA ile eksen bulma + cross-section radius hesaplama
   * @param positions Float32Array [x1,y1,z1, x2,y2,z2, ...]
   * @returns {center, axis, radius, quality} veya null
   */
  async detectCylinder(positions: Float32Array): Promise<{
    center: THREE.Vector3;
    axis: THREE.Vector3;
    radius: number;
    quality: number;
  } | null> {
    if (!this.available || !this.device || positions.length < 15) { // En az 5 nokta
      return null;
    }

    const vertexCount = positions.length / 3;
    const startTime = performance.now();

    // 1. Centroid hesapla (küre ile aynı shader)
    const centroidShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<storage, read_write> result: array<f32>;
      @group(0) @binding(2) var<uniform> vertexCount: u32;

      var<workgroup> localSums: array<vec4<f32>, 256>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
        let idx = gid.x;
        var sum = vec4<f32>(0.0, 0.0, 0.0, 0.0);

        if (idx < vertexCount) {
          let base = idx * 3u;
          sum = vec4<f32>(positions[base], positions[base + 1u], positions[base + 2u], 1.0);
        }

        localSums[lid.x] = sum;
        workgroupBarrier();

        for (var s = 128u; s > 0u; s = s >> 1u) {
          if (lid.x < s) {
            localSums[lid.x] = localSums[lid.x] + localSums[lid.x + s];
          }
          workgroupBarrier();
        }

        if (lid.x == 0u) {
          result[0] = result[0] + localSums[0].x;
          result[1] = result[1] + localSums[0].y;
          result[2] = result[2] + localSums[0].z;
          result[3] = result[3] + localSums[0].w;
        }
      }
    `;

    // 2. Kovaryans matrisi hesapla (PCA için)
    const covarianceShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<uniform> center: vec3<f32>;
      @group(0) @binding(2) var<storage, read_write> covariance: array<f32>; // 6 unique elements: xx, xy, xz, yy, yz, zz
      @group(0) @binding(3) var<uniform> vertexCount: u32;

      var<workgroup> localCov: array<array<f32, 6>, 256>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
        let idx = gid.x;
        var cov = array<f32, 6>(0.0, 0.0, 0.0, 0.0, 0.0, 0.0);

        if (idx < vertexCount) {
          let base = idx * 3u;
          let dx = positions[base] - center.x;
          let dy = positions[base + 1u] - center.y;
          let dz = positions[base + 2u] - center.z;

          cov[0] = dx * dx; // xx
          cov[1] = dx * dy; // xy
          cov[2] = dx * dz; // xz
          cov[3] = dy * dy; // yy
          cov[4] = dy * dz; // yz
          cov[5] = dz * dz; // zz
        }

        localCov[lid.x] = cov;
        workgroupBarrier();

        for (var s = 128u; s > 0u; s = s >> 1u) {
          if (lid.x < s) {
            for (var i = 0u; i < 6u; i = i + 1u) {
              localCov[lid.x][i] = localCov[lid.x][i] + localCov[lid.x + s][i];
            }
          }
          workgroupBarrier();
        }

        if (lid.x == 0u) {
          for (var i = 0u; i < 6u; i = i + 1u) {
            covariance[i] = covariance[i] + localCov[0][i];
          }
        }
      }
    `;

    // 3. Radial mesafe hesaplama (eksene göre)
    const radialDistanceShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<uniform> center: vec3<f32>;
      @group(0) @binding(2) var<uniform> axis: vec3<f32>;
      @group(0) @binding(3) var<storage, read_write> radialDistances: array<f32>;
      @group(0) @binding(4) var<uniform> vertexCount: u32;

      @compute @workgroup_size(64)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
        let idx = gid.x;
        if (idx >= vertexCount) {
          return;
        }

        let base = idx * 3u;
        let pos = vec3<f32>(positions[base], positions[base + 1u], positions[base + 2u]);

        // Noktanın merkeze göre vektörü
        let toPoint = pos - center;

        // Eksen üzerindeki projeksiyon
        let axialProj = dot(toPoint, axis);

        // Radial vektör = noktanın eksen bileşenini çıkar
        let radialVec = toPoint - axis * axialProj;

        // Radial mesafe
        radialDistances[idx] = length(radialVec);
      }
    `;

    try {
      // === ADIM 1: Centroid hesapla ===
      const posBuffer = this.device.createBuffer({
        size: positions.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(posBuffer.getMappedRange()).set(positions);
      posBuffer.unmap();

      const centroidResultBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const countBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Uint32Array(countBuffer.getMappedRange()).set([vertexCount]);
      countBuffer.unmap();

      const centroidModule = this.device.createShaderModule({ code: centroidShader });
      const centroidLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const centroidPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [centroidLayout] }),
        compute: { module: centroidModule, entryPoint: 'main' },
      });
      const centroidBindGroup = this.device.createBindGroup({
        layout: centroidLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centroidResultBuffer } },
          { binding: 2, resource: { buffer: countBuffer } },
        ],
      });

      let commandEncoder = this.device.createCommandEncoder();
      let passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(centroidPipeline);
      passEncoder.setBindGroup(0, centroidBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      passEncoder.end();

      const centroidStaging = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(centroidResultBuffer, 0, centroidStaging, 0, 16);
      this.device.queue.submit([commandEncoder.finish()]);

      await centroidStaging.mapAsync(GPUMapMode.READ);
      const centroidData = new Float32Array(centroidStaging.getMappedRange().slice(0));
      centroidStaging.unmap();

      const center = new THREE.Vector3(
        centroidData[0] / centroidData[3],
        centroidData[1] / centroidData[3],
        centroidData[2] / centroidData[3]
      );

      // === ADIM 2: Kovaryans matrisi hesapla ===
      const centerBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Float32Array(centerBuffer.getMappedRange()).set([center.x, center.y, center.z, 0]);
      centerBuffer.unmap();

      const covBuffer = this.device.createBuffer({
        size: 24, // 6 floats
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const covModule = this.device.createShaderModule({ code: covarianceShader });
      const covLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const covPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [covLayout] }),
        compute: { module: covModule, entryPoint: 'main' },
      });
      const covBindGroup = this.device.createBindGroup({
        layout: covLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: covBuffer } },
          { binding: 3, resource: { buffer: countBuffer } },
        ],
      });

      commandEncoder = this.device.createCommandEncoder();
      passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(covPipeline);
      passEncoder.setBindGroup(0, covBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      passEncoder.end();

      const covStaging = this.device.createBuffer({
        size: 24,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(covBuffer, 0, covStaging, 0, 24);
      this.device.queue.submit([commandEncoder.finish()]);

      await covStaging.mapAsync(GPUMapMode.READ);
      const covData = new Float32Array(covStaging.getMappedRange().slice(0));
      covStaging.unmap();

      // PCA: Power iteration ile ana eksen bul (CPU - küçük matris)
      const cov = [
        [covData[0], covData[1], covData[2]],
        [covData[1], covData[3], covData[4]],
        [covData[2], covData[4], covData[5]]
      ];

      let axis = new THREE.Vector3(1, 1, 1).normalize();
      for (let iter = 0; iter < 20; iter++) {
        const newAxis = new THREE.Vector3(
          cov[0][0] * axis.x + cov[0][1] * axis.y + cov[0][2] * axis.z,
          cov[1][0] * axis.x + cov[1][1] * axis.y + cov[1][2] * axis.z,
          cov[2][0] * axis.x + cov[2][1] * axis.y + cov[2][2] * axis.z
        );
        const len = newAxis.length();
        if (len < 0.0001) break;
        newAxis.divideScalar(len);
        axis = newAxis;
      }
      axis.normalize();

      // === ADIM 3: Radial mesafeleri GPU'da hesapla ===
      const axisBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Float32Array(axisBuffer.getMappedRange()).set([axis.x, axis.y, axis.z, 0]);
      axisBuffer.unmap();

      const radialBuffer = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const radialModule = this.device.createShaderModule({ code: radialDistanceShader });
      const radialLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const radialPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [radialLayout] }),
        compute: { module: radialModule, entryPoint: 'main' },
      });
      const radialBindGroup = this.device.createBindGroup({
        layout: radialLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: axisBuffer } },
          { binding: 3, resource: { buffer: radialBuffer } },
          { binding: 4, resource: { buffer: countBuffer } },
        ],
      });

      commandEncoder = this.device.createCommandEncoder();
      passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(radialPipeline);
      passEncoder.setBindGroup(0, radialBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 64));
      passEncoder.end();

      const radialStaging = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(radialBuffer, 0, radialStaging, 0, vertexCount * 4);
      this.device.queue.submit([commandEncoder.finish()]);

      await radialStaging.mapAsync(GPUMapMode.READ);
      const radialDistances = new Float32Array(radialStaging.getMappedRange().slice(0));
      radialStaging.unmap();

      // İstatistikler (CPU - küçük veri)
      let sum = 0, min = Infinity, max = -Infinity;
      for (let i = 0; i < radialDistances.length; i++) {
        sum += radialDistances[i];
        min = Math.min(min, radialDistances[i]);
        max = Math.max(max, radialDistances[i]);
      }
      const avgRadius = sum / radialDistances.length;

      const tolerance = Math.max(0.1, avgRadius * 0.05);
      let inToleranceCount = 0;
      for (let i = 0; i < radialDistances.length; i++) {
        if (Math.abs(radialDistances[i] - avgRadius) <= tolerance) {
          inToleranceCount++;
        }
      }
      const quality = (inToleranceCount / radialDistances.length) * 100;

      // Cleanup
      posBuffer.destroy();
      centroidResultBuffer.destroy();
      countBuffer.destroy();
      centerBuffer.destroy();
      covBuffer.destroy();
      axisBuffer.destroy();
      radialBuffer.destroy();
      centroidStaging.destroy();
      covStaging.destroy();
      radialStaging.destroy();

      const elapsed = performance.now() - startTime;

      // %85+ kalite ve radius varyasyonu düşükse silindir
      if (quality > 85 && (max - min) < avgRadius * 0.2) {
        return { center, axis, radius: avgRadius, quality };
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * 🚀 Koni tespiti - GPU paralel hesaplama
   * Silindir gibi eksen bulur, sonra eksen boyunca değişen yarıçapı hesaplar
   * @param positions Float32Array [x1,y1,z1, x2,y2,z2, ...]
   * @returns {apex, axis, angle, quality} veya null
   */
  async detectCone(positions: Float32Array): Promise<{
    apex: THREE.Vector3;
    axis: THREE.Vector3;
    angle: number; // Yarım açı (derece)
    quality: number;
  } | null> {
    if (!this.available || !this.device || positions.length < 15) {
      return null;
    }

    const vertexCount = positions.length / 3;
    const startTime = performance.now();

    // 1. Centroid ve PCA eksen hesapla (silindir ile aynı)
    const centroidShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<storage, read_write> result: array<f32>;
      @group(0) @binding(2) var<uniform> vertexCount: u32;

      var<workgroup> localSums: array<vec4<f32>, 256>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
        let idx = gid.x;
        var sum = vec4<f32>(0.0, 0.0, 0.0, 0.0);

        if (idx < vertexCount) {
          let base = idx * 3u;
          sum = vec4<f32>(positions[base], positions[base + 1u], positions[base + 2u], 1.0);
        }

        localSums[lid.x] = sum;
        workgroupBarrier();

        for (var s = 128u; s > 0u; s = s >> 1u) {
          if (lid.x < s) {
            localSums[lid.x] = localSums[lid.x] + localSums[lid.x + s];
          }
          workgroupBarrier();
        }

        if (lid.x == 0u) {
          result[0] = result[0] + localSums[0].x;
          result[1] = result[1] + localSums[0].y;
          result[2] = result[2] + localSums[0].z;
          result[3] = result[3] + localSums[0].w;
        }
      }
    `;

    const covarianceShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<uniform> center: vec3<f32>;
      @group(0) @binding(2) var<storage, read_write> covariance: array<f32>;
      @group(0) @binding(3) var<uniform> vertexCount: u32;

      var<workgroup> localCov: array<array<f32, 6>, 256>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
        let idx = gid.x;
        var cov = array<f32, 6>(0.0, 0.0, 0.0, 0.0, 0.0, 0.0);

        if (idx < vertexCount) {
          let base = idx * 3u;
          let dx = positions[base] - center.x;
          let dy = positions[base + 1u] - center.y;
          let dz = positions[base + 2u] - center.z;

          cov[0] = dx * dx;
          cov[1] = dx * dy;
          cov[2] = dx * dz;
          cov[3] = dy * dy;
          cov[4] = dy * dz;
          cov[5] = dz * dz;
        }

        localCov[lid.x] = cov;
        workgroupBarrier();

        for (var s = 128u; s > 0u; s = s >> 1u) {
          if (lid.x < s) {
            for (var i = 0u; i < 6u; i = i + 1u) {
              localCov[lid.x][i] = localCov[lid.x][i] + localCov[lid.x + s][i];
            }
          }
          workgroupBarrier();
        }

        if (lid.x == 0u) {
          for (var i = 0u; i < 6u; i = i + 1u) {
            covariance[i] = covariance[i] + localCov[0][i];
          }
        }
      }
    `;

    // 2. Koni için: Eksen boyunca pozisyon VE radyal mesafe hesapla
    const coneAnalysisShader = `
      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<uniform> center: vec3<f32>;
      @group(0) @binding(2) var<uniform> axis: vec3<f32>;
      @group(0) @binding(3) var<storage, read_write> coneData: array<f32>; // [axialPos, radialDist] * N
      @group(0) @binding(4) var<uniform> vertexCount: u32;

      @compute @workgroup_size(64)
      fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
        let idx = gid.x;
        if (idx >= vertexCount) {
          return;
        }

        let base = idx * 3u;
        let pos = vec3<f32>(positions[base], positions[base + 1u], positions[base + 2u]);

        let toPoint = pos - center;
        let axialProj = dot(toPoint, axis);
        let radialVec = toPoint - axis * axialProj;
        let radialDist = length(radialVec);

        // Her nokta için [axialPos, radialDist] çifti sakla
        coneData[idx * 2u] = axialProj;
        coneData[idx * 2u + 1u] = radialDist;
      }
    `;

    try {
      // === ADIM 1: Centroid hesapla ===
      const posBuffer = this.device.createBuffer({
        size: positions.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(posBuffer.getMappedRange()).set(positions);
      posBuffer.unmap();

      const centroidResultBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const countBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Uint32Array(countBuffer.getMappedRange()).set([vertexCount]);
      countBuffer.unmap();

      const centroidModule = this.device.createShaderModule({ code: centroidShader });
      const centroidLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const centroidPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [centroidLayout] }),
        compute: { module: centroidModule, entryPoint: 'main' },
      });
      const centroidBindGroup = this.device.createBindGroup({
        layout: centroidLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centroidResultBuffer } },
          { binding: 2, resource: { buffer: countBuffer } },
        ],
      });

      let commandEncoder = this.device.createCommandEncoder();
      let passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(centroidPipeline);
      passEncoder.setBindGroup(0, centroidBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      passEncoder.end();

      const centroidStaging = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(centroidResultBuffer, 0, centroidStaging, 0, 16);
      this.device.queue.submit([commandEncoder.finish()]);

      await centroidStaging.mapAsync(GPUMapMode.READ);
      const centroidData = new Float32Array(centroidStaging.getMappedRange().slice(0));
      centroidStaging.unmap();

      const center = new THREE.Vector3(
        centroidData[0] / centroidData[3],
        centroidData[1] / centroidData[3],
        centroidData[2] / centroidData[3]
      );

      // === ADIM 2: PCA ile eksen bul ===
      const centerBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Float32Array(centerBuffer.getMappedRange()).set([center.x, center.y, center.z, 0]);
      centerBuffer.unmap();

      const covBuffer = this.device.createBuffer({
        size: 24,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const covModule = this.device.createShaderModule({ code: covarianceShader });
      const covLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const covPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [covLayout] }),
        compute: { module: covModule, entryPoint: 'main' },
      });
      const covBindGroup = this.device.createBindGroup({
        layout: covLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: covBuffer } },
          { binding: 3, resource: { buffer: countBuffer } },
        ],
      });

      commandEncoder = this.device.createCommandEncoder();
      passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(covPipeline);
      passEncoder.setBindGroup(0, covBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      passEncoder.end();

      const covStaging = this.device.createBuffer({
        size: 24,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(covBuffer, 0, covStaging, 0, 24);
      this.device.queue.submit([commandEncoder.finish()]);

      await covStaging.mapAsync(GPUMapMode.READ);
      const covData = new Float32Array(covStaging.getMappedRange().slice(0));
      covStaging.unmap();

      // PCA: Power iteration
      const cov = [
        [covData[0], covData[1], covData[2]],
        [covData[1], covData[3], covData[4]],
        [covData[2], covData[4], covData[5]]
      ];

      let axis = new THREE.Vector3(1, 1, 1).normalize();
      for (let iter = 0; iter < 20; iter++) {
        const newAxis = new THREE.Vector3(
          cov[0][0] * axis.x + cov[0][1] * axis.y + cov[0][2] * axis.z,
          cov[1][0] * axis.x + cov[1][1] * axis.y + cov[1][2] * axis.z,
          cov[2][0] * axis.x + cov[2][1] * axis.y + cov[2][2] * axis.z
        );
        const len = newAxis.length();
        if (len < 0.0001) break;
        newAxis.divideScalar(len);
        axis = newAxis;
      }
      axis.normalize();

      // === ADIM 3: Koni analizi - eksen boyunca pozisyon ve radyal mesafe ===
      const axisBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM,
        mappedAtCreation: true,
      });
      new Float32Array(axisBuffer.getMappedRange()).set([axis.x, axis.y, axis.z, 0]);
      axisBuffer.unmap();

      const coneDataBuffer = this.device.createBuffer({
        size: vertexCount * 2 * 4, // [axial, radial] * N
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const coneModule = this.device.createShaderModule({ code: coneAnalysisShader });
      const coneLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const conePipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [coneLayout] }),
        compute: { module: coneModule, entryPoint: 'main' },
      });
      const coneBindGroup = this.device.createBindGroup({
        layout: coneLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: axisBuffer } },
          { binding: 3, resource: { buffer: coneDataBuffer } },
          { binding: 4, resource: { buffer: countBuffer } },
        ],
      });

      commandEncoder = this.device.createCommandEncoder();
      passEncoder = commandEncoder.beginComputePass();
      passEncoder.setPipeline(conePipeline);
      passEncoder.setBindGroup(0, coneBindGroup);
      passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 64));
      passEncoder.end();

      const coneStaging = this.device.createBuffer({
        size: vertexCount * 2 * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });
      commandEncoder.copyBufferToBuffer(coneDataBuffer, 0, coneStaging, 0, vertexCount * 2 * 4);
      this.device.queue.submit([commandEncoder.finish()]);

      await coneStaging.mapAsync(GPUMapMode.READ);
      const coneData = new Float32Array(coneStaging.getMappedRange().slice(0));
      coneStaging.unmap();

      // === ADIM 4: Linear regression ile koni açısı hesapla (CPU - küçük veri) ===
      // r = a * h + b (r: radyal mesafe, h: eksen pozisyonu)
      // Koni açısı: atan(a) * 180 / PI
      let sumH = 0, sumR = 0, sumHH = 0, sumHR = 0;
      let minH = Infinity, maxH = -Infinity;

      for (let i = 0; i < vertexCount; i++) {
        const h = coneData[i * 2];
        const r = coneData[i * 2 + 1];
        sumH += h;
        sumR += r;
        sumHH += h * h;
        sumHR += h * r;
        minH = Math.min(minH, h);
        maxH = Math.max(maxH, h);
      }

      const n = vertexCount;
      const denom = n * sumHH - sumH * sumH;

      if (Math.abs(denom) < 1e-10) {
        // Cleanup ve çık
        posBuffer.destroy();
        centroidResultBuffer.destroy();
        countBuffer.destroy();
        centerBuffer.destroy();
        covBuffer.destroy();
        axisBuffer.destroy();
        coneDataBuffer.destroy();
        centroidStaging.destroy();
        covStaging.destroy();
        coneStaging.destroy();
        return null;
      }

      const a = (n * sumHR - sumH * sumR) / denom; // Eğim (koni açısı için)
      const b = (sumR - a * sumH) / n; // Y-intercept (apex için)

      // Koni açısı (yarım açı - derece)
      const halfAngle = Math.atan(Math.abs(a)) * 180 / Math.PI;

      // Apex noktası: r = 0 olduğunda h değeri
      // a * h + b = 0 => h = -b / a
      const apexH = Math.abs(a) > 0.001 ? -b / a : 0;
      const apex = center.clone().add(axis.clone().multiplyScalar(apexH));

      // === Quality hesapla: Noktaların regresyon doğrusuna uyumu ===
      let totalError = 0;
      const tolerance = Math.max(0.5, Math.abs(a) * (maxH - minH) * 0.1);
      let inToleranceCount = 0;

      for (let i = 0; i < vertexCount; i++) {
        const h = coneData[i * 2];
        const r = coneData[i * 2 + 1];
        const expectedR = a * h + b;
        const error = Math.abs(r - expectedR);
        totalError += error;

        if (error <= tolerance) {
          inToleranceCount++;
        }
      }

      const quality = (inToleranceCount / vertexCount) * 100;

      // Cleanup
      posBuffer.destroy();
      centroidResultBuffer.destroy();
      countBuffer.destroy();
      centerBuffer.destroy();
      covBuffer.destroy();
      axisBuffer.destroy();
      coneDataBuffer.destroy();
      centroidStaging.destroy();
      covStaging.destroy();
      coneStaging.destroy();

      const elapsed = performance.now() - startTime;

      // Koni kriterleri:
      // - %80+ kalite
      // - Açı 1-85 derece arası (düz veya tam dik değil)
      // - a (eğim) düz sıfır değil (silindir değil)
      if (quality > 80 && halfAngle > 1 && halfAngle < 85 && Math.abs(a) > 0.01) {
        return { apex, axis, angle: halfAngle, quality };
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * 🚀 GPU Torus (Halka) Tespiti
   * Major radius (R) ve minor radius (r) hesaplama
   * Torus denklemi: (sqrt(x² + y²) - R)² + z² = r²
   */
  async detectTorus(positions: Float32Array): Promise<{
    center: THREE.Vector3;
    axis: THREE.Vector3;
    majorRadius: number;
    minorRadius: number;
    quality: number;
  } | null> {
    if (!this.available || !this.device) {
      throw new Error('WebGPU not available');
    }

    const startTime = performance.now();
    const vertexCount = positions.length / 3;

    if (vertexCount < 20) return null;

    try {
      // ADIM 1: Centroid hesapla (paralel reduction)
      const centroidShader = `
        @group(0) @binding(0) var<storage, read> positions: array<f32>;
        @group(0) @binding(1) var<storage, read_write> result: array<atomic<u32>>;
        @group(0) @binding(2) var<uniform> count: u32;

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let idx = id.x;
          if (idx >= count) { return; }

          let px = positions[idx * 3u];
          let py = positions[idx * 3u + 1u];
          let pz = positions[idx * 3u + 2u];

          // Atomic add için integer scaling (1000000x)
          atomicAdd(&result[0], u32(i32(px * 1000000.0)));
          atomicAdd(&result[1], u32(i32(py * 1000000.0)));
          atomicAdd(&result[2], u32(i32(pz * 1000000.0)));
        }
      `;

      // ADIM 2: Covariance matrix hesapla
      const covarianceShader = `
        @group(0) @binding(0) var<storage, read> positions: array<f32>;
        @group(0) @binding(1) var<uniform> center: vec3<f32>;
        @group(0) @binding(2) var<storage, read_write> cov: array<atomic<u32>>;
        @group(0) @binding(3) var<uniform> count: u32;

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let idx = id.x;
          if (idx >= count) { return; }

          let dx = positions[idx * 3u] - center.x;
          let dy = positions[idx * 3u + 1u] - center.y;
          let dz = positions[idx * 3u + 2u] - center.z;

          // Covariance elemanları (scaling 100000x)
          atomicAdd(&cov[0], u32(i32(dx * dx * 100000.0)));
          atomicAdd(&cov[1], u32(i32(dx * dy * 100000.0)));
          atomicAdd(&cov[2], u32(i32(dx * dz * 100000.0)));
          atomicAdd(&cov[3], u32(i32(dy * dy * 100000.0)));
          atomicAdd(&cov[4], u32(i32(dy * dz * 100000.0)));
          atomicAdd(&cov[5], u32(i32(dz * dz * 100000.0)));
        }
      `;

      // ADIM 3: Torus analizi - planar mesafe ve tube mesafe hesapla
      const torusAnalysisShader = `
        @group(0) @binding(0) var<storage, read> positions: array<f32>;
        @group(0) @binding(1) var<uniform> center: vec3<f32>;
        @group(0) @binding(2) var<uniform> axis: vec3<f32>;
        @group(0) @binding(3) var<storage, read_write> planarDists: array<f32>;
        @group(0) @binding(4) var<uniform> count: u32;

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let idx = id.x;
          if (idx >= count) { return; }

          let dx = positions[idx * 3u] - center.x;
          let dy = positions[idx * 3u + 1u] - center.y;
          let dz = positions[idx * 3u + 2u] - center.z;

          // Eksene dik düzleme projeksiyon
          let axialDist = dx * axis.x + dy * axis.y + dz * axis.z;
          let perpX = dx - axialDist * axis.x;
          let perpY = dy - axialDist * axis.y;
          let perpZ = dz - axialDist * axis.z;

          // Planar mesafe (major radius'a yakın olmalı)
          let planarDist = sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);
          planarDists[idx] = planarDist;
        }
      `;

      // Buffer'lar oluştur
      const posBuffer = this.device.createBuffer({
        size: positions.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(posBuffer.getMappedRange()).set(positions);
      posBuffer.unmap();

      const centroidResultBuffer = this.device.createBuffer({
        size: 12, // 3 x i32
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
        mappedAtCreation: true,
      });
      new Int32Array(centroidResultBuffer.getMappedRange()).set([0, 0, 0]);
      centroidResultBuffer.unmap();

      const countBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Uint32Array(countBuffer.getMappedRange()).set([vertexCount]);
      countBuffer.unmap();

      const centroidStaging = this.device.createBuffer({
        size: 12,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      // PASS 1: Centroid
      const centroidModule = this.device.createShaderModule({ code: centroidShader });
      const centroidLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const centroidBindGroup = this.device.createBindGroup({
        layout: centroidLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centroidResultBuffer } },
          { binding: 2, resource: { buffer: countBuffer } },
        ],
      });
      const centroidPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [centroidLayout] }),
        compute: { module: centroidModule, entryPoint: 'main' },
      });

      let encoder = this.device.createCommandEncoder();
      let pass = encoder.beginComputePass();
      pass.setPipeline(centroidPipeline);
      pass.setBindGroup(0, centroidBindGroup);
      pass.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      pass.end();
      encoder.copyBufferToBuffer(centroidResultBuffer, 0, centroidStaging, 0, 12);
      this.device.queue.submit([encoder.finish()]);

      await centroidStaging.mapAsync(GPUMapMode.READ);
      const centroidData = new Int32Array(centroidStaging.getMappedRange().slice(0));
      centroidStaging.unmap();

      const centerX = centroidData[0] / 1000000 / vertexCount;
      const centerY = centroidData[1] / 1000000 / vertexCount;
      const centerZ = centroidData[2] / 1000000 / vertexCount;

      // PASS 2: Covariance
      const centerBuffer = this.device.createBuffer({
        size: 16, // vec3 + padding
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(centerBuffer.getMappedRange()).set([centerX, centerY, centerZ, 0]);
      centerBuffer.unmap();

      const covBuffer = this.device.createBuffer({
        size: 24, // 6 x i32
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
        mappedAtCreation: true,
      });
      new Int32Array(covBuffer.getMappedRange()).set([0, 0, 0, 0, 0, 0]);
      covBuffer.unmap();

      const covStaging = this.device.createBuffer({
        size: 24,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      const covModule = this.device.createShaderModule({ code: covarianceShader });
      const covLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const covBindGroup = this.device.createBindGroup({
        layout: covLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: covBuffer } },
          { binding: 3, resource: { buffer: countBuffer } },
        ],
      });
      const covPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [covLayout] }),
        compute: { module: covModule, entryPoint: 'main' },
      });

      encoder = this.device.createCommandEncoder();
      pass = encoder.beginComputePass();
      pass.setPipeline(covPipeline);
      pass.setBindGroup(0, covBindGroup);
      pass.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      pass.end();
      encoder.copyBufferToBuffer(covBuffer, 0, covStaging, 0, 24);
      this.device.queue.submit([encoder.finish()]);

      await covStaging.mapAsync(GPUMapMode.READ);
      const covData = new Int32Array(covStaging.getMappedRange().slice(0));
      covStaging.unmap();

      // Covariance matrix'i oluştur (normalize)
      const scale = 100000 * vertexCount;
      const cov00 = covData[0] / scale;
      const cov01 = covData[1] / scale;
      const cov02 = covData[2] / scale;
      const cov11 = covData[3] / scale;
      const cov12 = covData[4] / scale;
      const cov22 = covData[5] / scale;

      // Power iteration - birinci eigenvector
      let v1x = 1, v1y = 0, v1z = 0;
      for (let iter = 0; iter < 20; iter++) {
        const newVx = cov00 * v1x + cov01 * v1y + cov02 * v1z;
        const newVy = cov01 * v1x + cov11 * v1y + cov12 * v1z;
        const newVz = cov02 * v1x + cov12 * v1y + cov22 * v1z;
        const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
        if (len > 0.0001) { v1x = newVx / len; v1y = newVy / len; v1z = newVz / len; }
      }

      // Deflation ve ikinci eigenvector
      const lambda1 = cov00 * v1x * v1x + 2 * cov01 * v1x * v1y + 2 * cov02 * v1x * v1z +
                      cov11 * v1y * v1y + 2 * cov12 * v1y * v1z + cov22 * v1z * v1z;
      const d00 = cov00 - lambda1 * v1x * v1x;
      const d01 = cov01 - lambda1 * v1x * v1y;
      const d02 = cov02 - lambda1 * v1x * v1z;
      const d11 = cov11 - lambda1 * v1y * v1y;
      const d12 = cov12 - lambda1 * v1y * v1z;
      const d22 = cov22 - lambda1 * v1z * v1z;

      let v2x = 0, v2y = 1, v2z = 0;
      for (let iter = 0; iter < 20; iter++) {
        const newVx = d00 * v2x + d01 * v2y + d02 * v2z;
        const newVy = d01 * v2x + d11 * v2y + d12 * v2z;
        const newVz = d02 * v2x + d12 * v2y + d22 * v2z;
        const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
        if (len > 0.0001) { v2x = newVx / len; v2y = newVy / len; v2z = newVz / len; }
      }

      // Normal axis = cross product (en küçük varyans)
      let axisX = v1y * v2z - v1z * v2y;
      let axisY = v1z * v2x - v1x * v2z;
      let axisZ = v1x * v2y - v1y * v2x;
      const axisLen = Math.sqrt(axisX * axisX + axisY * axisY + axisZ * axisZ);
      axisX /= axisLen; axisY /= axisLen; axisZ /= axisLen;

      // PASS 3: Planar distances (GPU)
      const axisBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(axisBuffer.getMappedRange()).set([axisX, axisY, axisZ, 0]);
      axisBuffer.unmap();

      const planarDistsBuffer = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const planarStaging = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      const torusModule = this.device.createShaderModule({ code: torusAnalysisShader });
      const torusLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const torusBindGroup = this.device.createBindGroup({
        layout: torusLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: axisBuffer } },
          { binding: 3, resource: { buffer: planarDistsBuffer } },
          { binding: 4, resource: { buffer: countBuffer } },
        ],
      });
      const torusPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [torusLayout] }),
        compute: { module: torusModule, entryPoint: 'main' },
      });

      encoder = this.device.createCommandEncoder();
      pass = encoder.beginComputePass();
      pass.setPipeline(torusPipeline);
      pass.setBindGroup(0, torusBindGroup);
      pass.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      pass.end();
      encoder.copyBufferToBuffer(planarDistsBuffer, 0, planarStaging, 0, vertexCount * 4);
      this.device.queue.submit([encoder.finish()]);

      await planarStaging.mapAsync(GPUMapMode.READ);
      const planarDists = new Float32Array(planarStaging.getMappedRange().slice(0));
      planarStaging.unmap();

      // Major radius = ortalama planar mesafe
      let sumPlanar = 0;
      for (let i = 0; i < vertexCount; i++) {
        sumPlanar += planarDists[i];
      }
      const majorRadius = sumPlanar / vertexCount;

      // Minor radius hesapla (CPU - tube mesafeleri)
      let sumTube = 0;
      const tubeDistances: number[] = [];
      for (let i = 0; i < vertexCount; i++) {
        const dx = positions[i * 3] - centerX;
        const dy = positions[i * 3 + 1] - centerY;
        const dz = positions[i * 3 + 2] - centerZ;
        const axialDist = dx * axisX + dy * axisY + dz * axisZ;
        const toTubeCenter = Math.abs(planarDists[i] - majorRadius);
        const tubeDist = Math.sqrt(toTubeCenter * toTubeCenter + axialDist * axialDist);
        tubeDistances.push(tubeDist);
        sumTube += tubeDist;
      }
      const minorRadius = sumTube / vertexCount;

      // Quality hesapla
      const tolerance = Math.max(0.1, minorRadius * 0.1);
      let inToleranceCount = 0;
      for (const dist of tubeDistances) {
        if (Math.abs(dist - minorRadius) <= tolerance) {
          inToleranceCount++;
        }
      }
      const quality = (inToleranceCount / vertexCount) * 100;

      // Cleanup
      posBuffer.destroy();
      centroidResultBuffer.destroy();
      countBuffer.destroy();
      centerBuffer.destroy();
      covBuffer.destroy();
      axisBuffer.destroy();
      planarDistsBuffer.destroy();
      centroidStaging.destroy();
      covStaging.destroy();
      planarStaging.destroy();

      // Torus kriterleri
      if (quality > 70 && majorRadius > minorRadius * 1.5 && minorRadius > 0.01) {
        return {
          center: new THREE.Vector3(centerX, centerY, centerZ),
          axis: new THREE.Vector3(axisX, axisY, axisZ),
          majorRadius,
          minorRadius,
          quality
        };
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * 🚀 GPU Düzlem Tespiti - PCA ile plane fitting
   * Normal vektör ve nokta döndürür
   */
  async detectPlane(positions: Float32Array): Promise<{
    point: THREE.Vector3;
    normal: THREE.Vector3;
    quality: number;
  } | null> {
    if (!this.available || !this.device) {
      throw new Error('WebGPU not available');
    }

    const startTime = performance.now();
    const vertexCount = positions.length / 3;

    if (vertexCount < 3) return null;

    try {
      // ADIM 1: Centroid hesapla
      const centroidShader = `
        @group(0) @binding(0) var<storage, read> positions: array<f32>;
        @group(0) @binding(1) var<storage, read_write> result: array<atomic<u32>>;
        @group(0) @binding(2) var<uniform> count: u32;

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let idx = id.x;
          if (idx >= count) { return; }

          let px = positions[idx * 3u];
          let py = positions[idx * 3u + 1u];
          let pz = positions[idx * 3u + 2u];

          atomicAdd(&result[0], u32(i32(px * 1000000.0)));
          atomicAdd(&result[1], u32(i32(py * 1000000.0)));
          atomicAdd(&result[2], u32(i32(pz * 1000000.0)));
        }
      `;

      // ADIM 2: Covariance matrix
      const covarianceShader = `
        @group(0) @binding(0) var<storage, read> positions: array<f32>;
        @group(0) @binding(1) var<uniform> center: vec3<f32>;
        @group(0) @binding(2) var<storage, read_write> cov: array<atomic<u32>>;
        @group(0) @binding(3) var<uniform> count: u32;

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let idx = id.x;
          if (idx >= count) { return; }

          let dx = positions[idx * 3u] - center.x;
          let dy = positions[idx * 3u + 1u] - center.y;
          let dz = positions[idx * 3u + 2u] - center.z;

          atomicAdd(&cov[0], u32(i32(dx * dx * 100000.0)));
          atomicAdd(&cov[1], u32(i32(dx * dy * 100000.0)));
          atomicAdd(&cov[2], u32(i32(dx * dz * 100000.0)));
          atomicAdd(&cov[3], u32(i32(dy * dy * 100000.0)));
          atomicAdd(&cov[4], u32(i32(dy * dz * 100000.0)));
          atomicAdd(&cov[5], u32(i32(dz * dz * 100000.0)));
        }
      `;

      // ADIM 3: Düzleme mesafe hesapla
      const planeDistShader = `
        @group(0) @binding(0) var<storage, read> positions: array<f32>;
        @group(0) @binding(1) var<uniform> center: vec3<f32>;
        @group(0) @binding(2) var<uniform> normal: vec3<f32>;
        @group(0) @binding(3) var<storage, read_write> distances: array<f32>;
        @group(0) @binding(4) var<uniform> count: u32;

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) id: vec3<u32>) {
          let idx = id.x;
          if (idx >= count) { return; }

          let dx = positions[idx * 3u] - center.x;
          let dy = positions[idx * 3u + 1u] - center.y;
          let dz = positions[idx * 3u + 2u] - center.z;

          // Düzleme dik mesafe
          let dist = abs(dx * normal.x + dy * normal.y + dz * normal.z);
          distances[idx] = dist;
        }
      `;

      // Buffer'lar
      const posBuffer = this.device.createBuffer({
        size: positions.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(posBuffer.getMappedRange()).set(positions);
      posBuffer.unmap();

      const centroidResultBuffer = this.device.createBuffer({
        size: 12,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
        mappedAtCreation: true,
      });
      new Int32Array(centroidResultBuffer.getMappedRange()).set([0, 0, 0]);
      centroidResultBuffer.unmap();

      const countBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Uint32Array(countBuffer.getMappedRange()).set([vertexCount]);
      countBuffer.unmap();

      const centroidStaging = this.device.createBuffer({
        size: 12,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      // PASS 1: Centroid
      const centroidModule = this.device.createShaderModule({ code: centroidShader });
      const centroidLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const centroidBindGroup = this.device.createBindGroup({
        layout: centroidLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centroidResultBuffer } },
          { binding: 2, resource: { buffer: countBuffer } },
        ],
      });
      const centroidPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [centroidLayout] }),
        compute: { module: centroidModule, entryPoint: 'main' },
      });

      let encoder = this.device.createCommandEncoder();
      let pass = encoder.beginComputePass();
      pass.setPipeline(centroidPipeline);
      pass.setBindGroup(0, centroidBindGroup);
      pass.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      pass.end();
      encoder.copyBufferToBuffer(centroidResultBuffer, 0, centroidStaging, 0, 12);
      this.device.queue.submit([encoder.finish()]);

      await centroidStaging.mapAsync(GPUMapMode.READ);
      const centroidData = new Int32Array(centroidStaging.getMappedRange().slice(0));
      centroidStaging.unmap();

      const centerX = centroidData[0] / 1000000 / vertexCount;
      const centerY = centroidData[1] / 1000000 / vertexCount;
      const centerZ = centroidData[2] / 1000000 / vertexCount;

      // PASS 2: Covariance
      const centerBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(centerBuffer.getMappedRange()).set([centerX, centerY, centerZ, 0]);
      centerBuffer.unmap();

      const covBuffer = this.device.createBuffer({
        size: 24,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
        mappedAtCreation: true,
      });
      new Int32Array(covBuffer.getMappedRange()).set([0, 0, 0, 0, 0, 0]);
      covBuffer.unmap();

      const covStaging = this.device.createBuffer({
        size: 24,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      const covModule = this.device.createShaderModule({ code: covarianceShader });
      const covLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const covBindGroup = this.device.createBindGroup({
        layout: covLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: covBuffer } },
          { binding: 3, resource: { buffer: countBuffer } },
        ],
      });
      const covPipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [covLayout] }),
        compute: { module: covModule, entryPoint: 'main' },
      });

      encoder = this.device.createCommandEncoder();
      pass = encoder.beginComputePass();
      pass.setPipeline(covPipeline);
      pass.setBindGroup(0, covBindGroup);
      pass.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      pass.end();
      encoder.copyBufferToBuffer(covBuffer, 0, covStaging, 0, 24);
      this.device.queue.submit([encoder.finish()]);

      await covStaging.mapAsync(GPUMapMode.READ);
      const covData = new Int32Array(covStaging.getMappedRange().slice(0));
      covStaging.unmap();

      // Covariance matrix
      const scale = 100000 * vertexCount;
      const cov00 = covData[0] / scale;
      const cov01 = covData[1] / scale;
      const cov02 = covData[2] / scale;
      const cov11 = covData[3] / scale;
      const cov12 = covData[4] / scale;
      const cov22 = covData[5] / scale;

      // Power iteration - en büyük iki eigenvector
      let v1x = 1, v1y = 0, v1z = 0;
      for (let iter = 0; iter < 20; iter++) {
        const newVx = cov00 * v1x + cov01 * v1y + cov02 * v1z;
        const newVy = cov01 * v1x + cov11 * v1y + cov12 * v1z;
        const newVz = cov02 * v1x + cov12 * v1y + cov22 * v1z;
        const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
        if (len > 0.0001) { v1x = newVx / len; v1y = newVy / len; v1z = newVz / len; }
      }

      // Deflation
      const lambda1 = cov00 * v1x * v1x + 2 * cov01 * v1x * v1y + 2 * cov02 * v1x * v1z +
                      cov11 * v1y * v1y + 2 * cov12 * v1y * v1z + cov22 * v1z * v1z;
      const d00 = cov00 - lambda1 * v1x * v1x;
      const d01 = cov01 - lambda1 * v1x * v1y;
      const d02 = cov02 - lambda1 * v1x * v1z;
      const d11 = cov11 - lambda1 * v1y * v1y;
      const d12 = cov12 - lambda1 * v1y * v1z;
      const d22 = cov22 - lambda1 * v1z * v1z;

      let v2x = 0, v2y = 1, v2z = 0;
      for (let iter = 0; iter < 20; iter++) {
        const newVx = d00 * v2x + d01 * v2y + d02 * v2z;
        const newVy = d01 * v2x + d11 * v2y + d12 * v2z;
        const newVz = d02 * v2x + d12 * v2y + d22 * v2z;
        const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
        if (len > 0.0001) { v2x = newVx / len; v2y = newVy / len; v2z = newVz / len; }
      }

      // Normal = cross product (en küçük varyans yönü)
      let normalX = v1y * v2z - v1z * v2y;
      let normalY = v1z * v2x - v1x * v2z;
      let normalZ = v1x * v2y - v1y * v2x;
      const normalLen = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);
      if (normalLen < 0.0001) {
        posBuffer.destroy();
        centroidResultBuffer.destroy();
        countBuffer.destroy();
        centerBuffer.destroy();
        covBuffer.destroy();
        centroidStaging.destroy();
        covStaging.destroy();
        return null;
      }
      normalX /= normalLen; normalY /= normalLen; normalZ /= normalLen;

      // PASS 3: Düzleme mesafeler (GPU)
      const normalBuffer = this.device.createBuffer({
        size: 16,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        mappedAtCreation: true,
      });
      new Float32Array(normalBuffer.getMappedRange()).set([normalX, normalY, normalZ, 0]);
      normalBuffer.unmap();

      const distsBuffer = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      });

      const distsStaging = this.device.createBuffer({
        size: vertexCount * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      const planeModule = this.device.createShaderModule({ code: planeDistShader });
      const planeLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
          { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        ],
      });
      const planeBindGroup = this.device.createBindGroup({
        layout: planeLayout,
        entries: [
          { binding: 0, resource: { buffer: posBuffer } },
          { binding: 1, resource: { buffer: centerBuffer } },
          { binding: 2, resource: { buffer: normalBuffer } },
          { binding: 3, resource: { buffer: distsBuffer } },
          { binding: 4, resource: { buffer: countBuffer } },
        ],
      });
      const planePipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({ bindGroupLayouts: [planeLayout] }),
        compute: { module: planeModule, entryPoint: 'main' },
      });

      encoder = this.device.createCommandEncoder();
      pass = encoder.beginComputePass();
      pass.setPipeline(planePipeline);
      pass.setBindGroup(0, planeBindGroup);
      pass.dispatchWorkgroups(Math.ceil(vertexCount / 256));
      pass.end();
      encoder.copyBufferToBuffer(distsBuffer, 0, distsStaging, 0, vertexCount * 4);
      this.device.queue.submit([encoder.finish()]);

      await distsStaging.mapAsync(GPUMapMode.READ);
      const distances = new Float32Array(distsStaging.getMappedRange().slice(0));
      distsStaging.unmap();

      // Quality hesapla
      let sumDist = 0;
      for (let i = 0; i < vertexCount; i++) {
        sumDist += distances[i];
      }
      const avgDist = sumDist / vertexCount;

      const tolerance = Math.max(0.01, avgDist * 2);
      let inToleranceCount = 0;
      for (let i = 0; i < vertexCount; i++) {
        if (distances[i] <= tolerance) {
          inToleranceCount++;
        }
      }
      const quality = (inToleranceCount / vertexCount) * 100;

      // Cleanup
      posBuffer.destroy();
      centroidResultBuffer.destroy();
      countBuffer.destroy();
      centerBuffer.destroy();
      covBuffer.destroy();
      normalBuffer.destroy();
      distsBuffer.destroy();
      centroidStaging.destroy();
      covStaging.destroy();
      distsStaging.destroy();

      // Düzlem kriterleri - %90+ kalite
      if (quality > 90) {
        return {
          point: new THREE.Vector3(centerX, centerY, centerZ),
          normal: new THREE.Vector3(normalX, normalY, normalZ),
          quality
        };
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * 🚀 Batch offset yönü hesaplama - Birden fazla ölçüm çizgisi için
   * Her çizgi için kameraya göre offset yönünü paralel hesaplar
   * @param lineData Array of {start: vec3, end: vec3} - Çizgi başlangıç ve bitiş noktaları
   * @param cameraPosition Kamera pozisyonu
   * @param cameraUp Kamera yukarı vektörü
   * @returns Float32Array - Her çizgi için offset yönü (x,y,z) * n
   */
  async computeBatchOffsetDirections(
    lineData: Float32Array, // [start1.x, start1.y, start1.z, end1.x, end1.y, end1.z, start2.x, ...]
    cameraPosition: { x: number; y: number; z: number },
    cameraUp: { x: number; y: number; z: number }
  ): Promise<Float32Array> {
    if (!this.available || !this.device) {
      throw new Error('WebGPU not available');
    }

    const lineCount = lineData.length / 6; // Her çizgi 6 float (start + end)
    const startTime = performance.now();

    const shaderCode = `
      struct CameraData {
        position: vec3<f32>,
        up: vec3<f32>,
      }

      @group(0) @binding(0) var<storage, read> lineData: array<f32>;
      @group(0) @binding(1) var<uniform> camera: CameraData;
      @group(0) @binding(2) var<storage, read_write> offsetDirs: array<f32>;
      @group(0) @binding(3) var<uniform> lineCount: u32;

      @compute @workgroup_size(64)
      fn main(@builtin(global_invocation_id) id: vec3<u32>) {
        let idx = id.x;
        if (idx >= lineCount) {
          return;
        }

        // Çizgi verilerini oku
        let baseIdx = idx * 6u;
        let start = vec3<f32>(lineData[baseIdx], lineData[baseIdx + 1u], lineData[baseIdx + 2u]);
        let end = vec3<f32>(lineData[baseIdx + 3u], lineData[baseIdx + 4u], lineData[baseIdx + 5u]);

        // Çizgi yön vektörü
        let dirVec = normalize(end - start);

        // Orta nokta
        let midPoint = (start + end) * 0.5;

        // Kameradan orta noktaya vektör
        let cameraVec = normalize(midPoint - camera.position);

        // Cross product ile offset yönü
        var offsetDir = cross(dirVec, cameraVec);
        let lenSq = dot(offsetDir, offsetDir);

        // Eğer çok küçükse (paralel), kamera up vektörünü kullan
        if (lenSq < 0.01) {
          offsetDir = cross(dirVec, camera.up);
        }

        offsetDir = normalize(offsetDir);

        // Offset yönünü kameraya doğru çevir
        if (dot(offsetDir, cameraVec) < 0.0) {
          offsetDir = -offsetDir;
        }

        // Y negatifse ters çevir (yukarı yönlü olsun)
        if (offsetDir.y < 0.0) {
          offsetDir = -offsetDir;
        }

        // Sonucu yaz
        let outIdx = idx * 3u;
        offsetDirs[outIdx] = offsetDir.x;
        offsetDirs[outIdx + 1u] = offsetDir.y;
        offsetDirs[outIdx + 2u] = offsetDir.z;
      }
    `;

    const shaderModule = this.device.createShaderModule({ code: shaderCode });

    // Line data buffer
    const lineBuffer = this.device.createBuffer({
      size: lineData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(lineBuffer.getMappedRange()).set(lineData);
    lineBuffer.unmap();

    // Camera data buffer (32 byte aligned)
    const cameraData = new Float32Array([
      cameraPosition.x, cameraPosition.y, cameraPosition.z, 0, // vec3 + padding
      cameraUp.x, cameraUp.y, cameraUp.z, 0 // vec3 + padding
    ]);
    const cameraBuffer = this.device.createBuffer({
      size: cameraData.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(cameraBuffer.getMappedRange()).set(cameraData);
    cameraBuffer.unmap();

    // Output buffer
    const outputSize = lineCount * 3 * 4; // lineCount * 3 floats * 4 bytes
    const outputBuffer = this.device.createBuffer({
      size: outputSize,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });

    // Line count buffer
    const countBuffer = this.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Uint32Array(countBuffer.getMappedRange()).set([lineCount]);
    countBuffer.unmap();

    // Staging buffer
    const stagingBuffer = this.device.createBuffer({
      size: outputSize,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });

    const bindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
      ],
    });

    const bindGroup = this.device.createBindGroup({
      layout: bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: lineBuffer } },
        { binding: 1, resource: { buffer: cameraBuffer } },
        { binding: 2, resource: { buffer: outputBuffer } },
        { binding: 3, resource: { buffer: countBuffer } },
      ],
    });

    const pipeline = this.device.createComputePipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
      compute: { module: shaderModule, entryPoint: 'main' },
    });

    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(pipeline);
    passEncoder.setBindGroup(0, bindGroup);
    passEncoder.dispatchWorkgroups(Math.ceil(lineCount / 64));
    passEncoder.end();

    commandEncoder.copyBufferToBuffer(outputBuffer, 0, stagingBuffer, 0, outputSize);
    this.device.queue.submit([commandEncoder.finish()]);

    await stagingBuffer.mapAsync(GPUMapMode.READ);
    const resultArray = new Float32Array(stagingBuffer.getMappedRange().slice(0));
    stagingBuffer.unmap();

    // Cleanup
    lineBuffer.destroy();
    cameraBuffer.destroy();
    outputBuffer.destroy();
    countBuffer.destroy();
    stagingBuffer.destroy();

    const elapsed = performance.now() - startTime;

    return resultArray;
  }

  /**
   * 🚀 İki geometri arasındaki en yakın nokta çiftini GPU'da hesapla
   * @returns { mesafe, nokta1Index, nokta2Index }
   */
  async computeClosestPoints(positions1: Float32Array, positions2: Float32Array): Promise<{
    mesafe: number;
    nokta1Index: number;
    nokta2Index: number;
  }> {
    if (!this.available || !this.device) {
      throw new Error('WebGPU not available');
    }

    const startTime = performance.now();
    const vertexCount1 = positions1.length / 3;
    const vertexCount2 = positions2.length / 3;

    // Compute shader - En yakın nokta çiftini bul
    const shaderCode = `
      struct ClosestResult {
        minDist: atomic<u32>,
        idx1: atomic<u32>,
        idx2: atomic<u32>,
      }

      @group(0) @binding(0) var<storage, read> positions1: array<f32>;
      @group(0) @binding(1) var<storage, read> positions2: array<f32>;
      @group(0) @binding(2) var<storage, read_write> result: ClosestResult;
      @group(0) @binding(3) var<uniform> counts: vec2<u32>;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) id: vec3<u32>) {
        let idx1 = id.x;
        let count1 = counts.x;
        let count2 = counts.y;

        if (idx1 >= count1) {
          return;
        }

        let p1x = positions1[idx1 * 3u];
        let p1y = positions1[idx1 * 3u + 1u];
        let p1z = positions1[idx1 * 3u + 2u];

        var localMinDist: f32 = 999999.0;
        var localMinIdx2: u32 = 0u;

        for (var idx2: u32 = 0u; idx2 < count2; idx2 = idx2 + 1u) {
          let p2x = positions2[idx2 * 3u];
          let p2y = positions2[idx2 * 3u + 1u];
          let p2z = positions2[idx2 * 3u + 2u];

          let dx = p1x - p2x;
          let dy = p1y - p2y;
          let dz = p1z - p2z;
          let dist = sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < localMinDist) {
            localMinDist = dist;
            localMinIdx2 = idx2;
          }
        }

        // Atomic min güncelle
        let distAsUint = bitcast<u32>(localMinDist);
        let oldDist = atomicMin(&result.minDist, distAsUint);

        // Eğer bu thread kazandıysa index'leri güncelle
        if (distAsUint <= oldDist) {
          atomicStore(&result.idx1, idx1);
          atomicStore(&result.idx2, localMinIdx2);
        }
      }
    `;

    const shaderModule = this.device.createShaderModule({ code: shaderCode });

    // Buffer'lar
    const positions1Buffer = this.device.createBuffer({
      size: positions1.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(positions1Buffer.getMappedRange()).set(positions1);
    positions1Buffer.unmap();

    const positions2Buffer = this.device.createBuffer({
      size: positions2.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(positions2Buffer.getMappedRange()).set(positions2);
    positions2Buffer.unmap();

    const resultBuffer = this.device.createBuffer({
      size: 12, // 3 x u32
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      mappedAtCreation: true,
    });
    new Uint32Array(resultBuffer.getMappedRange()).set([0x7F800000, 0, 0]); // infinity, 0, 0
    resultBuffer.unmap();

    const countsBuffer = this.device.createBuffer({
      size: 8, // vec2<u32>
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Uint32Array(countsBuffer.getMappedRange()).set([vertexCount1, vertexCount2]);
    countsBuffer.unmap();

    const stagingBuffer = this.device.createBuffer({
      size: 12,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });

    const bindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
      ],
    });

    const bindGroup = this.device.createBindGroup({
      layout: bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: positions1Buffer } },
        { binding: 1, resource: { buffer: positions2Buffer } },
        { binding: 2, resource: { buffer: resultBuffer } },
        { binding: 3, resource: { buffer: countsBuffer } },
      ],
    });

    const pipeline = this.device.createComputePipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
      compute: { module: shaderModule, entryPoint: 'main' },
    });

    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(pipeline);
    passEncoder.setBindGroup(0, bindGroup);
    passEncoder.dispatchWorkgroups(Math.ceil(vertexCount1 / 256));
    passEncoder.end();

    commandEncoder.copyBufferToBuffer(resultBuffer, 0, stagingBuffer, 0, 12);
    this.device.queue.submit([commandEncoder.finish()]);

    await stagingBuffer.mapAsync(GPUMapMode.READ);
    const resultArray = new Uint32Array(stagingBuffer.getMappedRange());
    const minDistAsUint = resultArray[0];
    const idx1 = resultArray[1];
    const idx2 = resultArray[2];
    const minDistance = new Float32Array(new Uint32Array([minDistAsUint]).buffer)[0];
    stagingBuffer.unmap();

    // Cleanup
    positions1Buffer.destroy();
    positions2Buffer.destroy();
    resultBuffer.destroy();
    countsBuffer.destroy();
    stagingBuffer.destroy();

    const elapsed = performance.now() - startTime;

    return { mesafe: minDistance, nokta1Index: idx1, nokta2Index: idx2 };
  }

  /**
   * 🚀 Çizgiden yüzeye dik mesafe hesapla (tüm çizgi noktalarından düzleme)
   * @param linePositions Çizgi vertex'leri
   * @param planeNormal Düzlem normal vektörü
   * @param planePoint Düzlem üzerindeki bir nokta
   * @returns En kısa dik mesafe ve index
   */
  async computeLineToPlaneDistance(
    linePositions: Float32Array,
    planeNormal: { x: number; y: number; z: number },
    planePoint: { x: number; y: number; z: number }
  ): Promise<{ mesafe: number; noktaIndex: number }> {
    if (!this.available || !this.device) {
      throw new Error('WebGPU not available');
    }

    const startTime = performance.now();
    const vertexCount = linePositions.length / 3;

    const shaderCode = `
      struct PlaneDistResult {
        minDist: atomic<u32>,
        minIdx: atomic<u32>,
      }

      struct PlaneData {
        normal: vec3<f32>,
        point: vec3<f32>,
      }

      @group(0) @binding(0) var<storage, read> positions: array<f32>;
      @group(0) @binding(1) var<uniform> plane: PlaneData;
      @group(0) @binding(2) var<storage, read_write> result: PlaneDistResult;
      @group(0) @binding(3) var<uniform> count: u32;

      @compute @workgroup_size(256)
      fn main(@builtin(global_invocation_id) id: vec3<u32>) {
        let idx = id.x;
        if (idx >= count) {
          return;
        }

        let px = positions[idx * 3u];
        let py = positions[idx * 3u + 1u];
        let pz = positions[idx * 3u + 2u];

        // Düzleme dik mesafe: |dot(p - planePoint, normal)|
        let dx = px - plane.point.x;
        let dy = py - plane.point.y;
        let dz = pz - plane.point.z;

        let dist = abs(dx * plane.normal.x + dy * plane.normal.y + dz * plane.normal.z);
        let distAsUint = bitcast<u32>(dist);

        let oldDist = atomicMin(&result.minDist, distAsUint);
        if (distAsUint <= oldDist) {
          atomicStore(&result.minIdx, idx);
        }
      }
    `;

    const shaderModule = this.device.createShaderModule({ code: shaderCode });

    const positionsBuffer = this.device.createBuffer({
      size: linePositions.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(positionsBuffer.getMappedRange()).set(linePositions);
    positionsBuffer.unmap();

    // Plane data (32 byte aligned)
    const planeData = new Float32Array([
      planeNormal.x, planeNormal.y, planeNormal.z, 0, // vec3 + padding
      planePoint.x, planePoint.y, planePoint.z, 0     // vec3 + padding
    ]);
    const planeBuffer = this.device.createBuffer({
      size: planeData.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Float32Array(planeBuffer.getMappedRange()).set(planeData);
    planeBuffer.unmap();

    const resultBuffer = this.device.createBuffer({
      size: 8,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
      mappedAtCreation: true,
    });
    new Uint32Array(resultBuffer.getMappedRange()).set([0x7F800000, 0]);
    resultBuffer.unmap();

    const countBuffer = this.device.createBuffer({
      size: 4,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    new Uint32Array(countBuffer.getMappedRange()).set([vertexCount]);
    countBuffer.unmap();

    const stagingBuffer = this.device.createBuffer({
      size: 8,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });

    const bindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
      ],
    });

    const bindGroup = this.device.createBindGroup({
      layout: bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: positionsBuffer } },
        { binding: 1, resource: { buffer: planeBuffer } },
        { binding: 2, resource: { buffer: resultBuffer } },
        { binding: 3, resource: { buffer: countBuffer } },
      ],
    });

    const pipeline = this.device.createComputePipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
      compute: { module: shaderModule, entryPoint: 'main' },
    });

    const commandEncoder = this.device.createCommandEncoder();
    const passEncoder = commandEncoder.beginComputePass();
    passEncoder.setPipeline(pipeline);
    passEncoder.setBindGroup(0, bindGroup);
    passEncoder.dispatchWorkgroups(Math.ceil(vertexCount / 256));
    passEncoder.end();

    commandEncoder.copyBufferToBuffer(resultBuffer, 0, stagingBuffer, 0, 8);
    this.device.queue.submit([commandEncoder.finish()]);

    await stagingBuffer.mapAsync(GPUMapMode.READ);
    const resultArray = new Uint32Array(stagingBuffer.getMappedRange());
    const minDistAsUint = resultArray[0];
    const minIdx = resultArray[1];
    const minDistance = new Float32Array(new Uint32Array([minDistAsUint]).buffer)[0];
    stagingBuffer.unmap();

    // Cleanup
    positionsBuffer.destroy();
    planeBuffer.destroy();
    resultBuffer.destroy();
    countBuffer.destroy();
    stagingBuffer.destroy();

    const elapsed = performance.now() - startTime;

    return { mesafe: minDistance, noktaIndex: minIdx };
  }
}

/**
 * 🎯 KATMAN 3: WebGL2 Transform Feedback (Fallback)
 * WebGPU yoksa WebGL2 ile GPU hesaplama
 */
export class WebGL2ComputeEngine {
  private gl: WebGL2RenderingContext | null = null;
  private available = false;

  initialize(renderer: THREE.WebGLRenderer): boolean {
    try {
      const gl = renderer.getContext() as WebGL2RenderingContext;
      if (!gl) return false;

      this.gl = gl;
      this.available = true;

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Transform Feedback ile GPU'da vertex işleme
   * (WebGPU'ya göre daha yavaş ama CPU'dan hızlı)
   */
  async computeMinDistance(positions1: Float32Array, positions2: Float32Array): Promise<number> {
    // TODO: Transform Feedback implementation
    // Şimdilik fallback
    throw new Error('WebGL2 compute not yet implemented - use WASM fallback');
  }

  dispose(): void {
    this.gl = null;
    this.available = false;
  }
}

/**
 * 🎯 ANA MOTOR: Tüm katmanları yöneten hibrid sistem
 * Otomatik olarak en hızlı yöntemi seçer
 */
export class GPUOlcumMotoru {
  private bvhOptimizer: BVHRaycastOptimizer;
  private webGPUEngine: WebGPUComputeEngine | null = null;
  private webGL2Engine: WebGL2ComputeEngine | null = null;

  private accelerationMode: 'webgpu' | 'webgl2' | 'wasm' | 'cpu' = 'cpu';
  private initialized = false;

  // Performans metrikleri
  private metrics = {
    raycastCount: 0,
    raycastTotalTime: 0,
    computeCount: 0,
    computeTotalTime: 0,
  };

  constructor() {
    this.bvhOptimizer = new BVHRaycastOptimizer();
  }

  /**
   * Motor'u başlat - en iyi GPU modunu otomatik seç
   */
  async initialize(renderer?: THREE.WebGLRenderer): Promise<void> {
    if (this.initialized) return;

    // WebGPU dene (en hızlı)
    const webGPUAvailable = await GPUCapabilityDetector.checkWebGPU();
    if (webGPUAvailable) {
      this.webGPUEngine = new WebGPUComputeEngine();
      const success = await this.webGPUEngine.initialize();
      if (success) {
        this.accelerationMode = 'webgpu';
        this.initialized = true;
        return;
      }
    }

    // WebGL2 dene (fallback)
    if (renderer) {
      const webGL2Available = GPUCapabilityDetector.checkWebGL2();
      if (webGL2Available) {
        this.webGL2Engine = new WebGL2ComputeEngine();
        const success = this.webGL2Engine.initialize(renderer);
        if (success) {
          this.accelerationMode = 'webgl2';
          this.initialized = true;
          return;
        }
      }
    }

    // WASM fallback (CPU'dan hızlı ama GPU'dan yavaş)
    this.accelerationMode = 'wasm';
    this.initialized = true;
  }

  /**
   * Scene'i optimize et (BVH)
   */
  optimizeScene(scene: THREE.Scene): void {
    this.bvhOptimizer.optimizeScene(scene);
  }

  /**
   * Mesh'i optimize et (BVH)
   */
  optimizeMesh(mesh: THREE.Mesh): void {
    this.bvhOptimizer.optimizeMesh(mesh);
  }

  /**
   * İki geometri arasındaki minimum mesafe - GPU accelerated
   */
  async computeMinDistance(geom1: THREE.BufferGeometry, geom2: THREE.BufferGeometry): Promise<number> {
    const startTime = performance.now();

    const pos1 = geom1.attributes.position.array as Float32Array;
    const pos2 = geom2.attributes.position.array as Float32Array;

    let result: number;

    // GPU compute şart (WebGPU öncelikli)
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        result = await this.webGPUEngine.computeMinDistance(pos1, pos2);
      } catch (e) {
        result = this.cpuFallbackMinDistance(pos1, pos2);
      }
    } else if (this.accelerationMode === 'webgl2' && this.webGL2Engine) {
      try {
        result = await this.webGL2Engine.computeMinDistance(pos1, pos2);
      } catch (e) {
        result = this.cpuFallbackMinDistance(pos1, pos2);
      }
    } else {
      // WASM/CPU fallback
      result = this.cpuFallbackMinDistance(pos1, pos2);
    }

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return result;
  }

  /**
   * 🚀 İki geometri arasındaki en yakın nokta çiftini GPU'da hesapla
   * @returns { mesafe, nokta1, nokta2 }
   */
  async computeClosestPoints(geom1: THREE.BufferGeometry, geom2: THREE.BufferGeometry): Promise<{
    mesafe: number;
    nokta1: THREE.Vector3;
    nokta2: THREE.Vector3;
  }> {
    const startTime = performance.now();

    const pos1 = geom1.attributes.position.array as Float32Array;
    const pos2 = geom2.attributes.position.array as Float32Array;

    // GPU compute (WebGPU öncelikli)
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.computeClosestPoints(pos1, pos2);
        const nokta1 = new THREE.Vector3(
          pos1[result.nokta1Index * 3],
          pos1[result.nokta1Index * 3 + 1],
          pos1[result.nokta1Index * 3 + 2]
        );
        const nokta2 = new THREE.Vector3(
          pos2[result.nokta2Index * 3],
          pos2[result.nokta2Index * 3 + 1],
          pos2[result.nokta2Index * 3 + 2]
        );

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return { mesafe: result.mesafe, nokta1, nokta2 };
      } catch (e) {
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackClosestPoints(pos1, pos2);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * 🚀 Çizgiden düzleme en kısa dik mesafe - GPU accelerated
   */
  async computeLineToPlaneDistance(
    cizgiGeom: THREE.BufferGeometry,
    planeNormal: THREE.Vector3,
    planePoint: THREE.Vector3
  ): Promise<{ mesafe: number; noktaIndex: number; enYakinNokta: THREE.Vector3 }> {
    const startTime = performance.now();
    const linePos = cizgiGeom.attributes.position.array as Float32Array;

    // GPU compute (WebGPU öncelikli)
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.computeLineToPlaneDistance(
          linePos,
          { x: planeNormal.x, y: planeNormal.y, z: planeNormal.z },
          { x: planePoint.x, y: planePoint.y, z: planePoint.z }
        );
        const enYakinNokta = new THREE.Vector3(
          linePos[result.noktaIndex * 3],
          linePos[result.noktaIndex * 3 + 1],
          linePos[result.noktaIndex * 3 + 2]
        );

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return { mesafe: result.mesafe, noktaIndex: result.noktaIndex, enYakinNokta };
      } catch (e) {
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackLineToPlane(linePos, planeNormal, planePoint);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * 🚀 Batch offset yönü hesaplama - Birden fazla ölçüm çizgisi için GPU accelerated
   * @param lines Array of {start: THREE.Vector3, end: THREE.Vector3}
   * @param camera THREE.Camera
   * @returns Array of THREE.Vector3 offset directions
   */
  async computeBatchOffsetDirections(
    lines: Array<{ start: THREE.Vector3; end: THREE.Vector3 }>,
    camera: THREE.Camera
  ): Promise<THREE.Vector3[]> {
    const startTime = performance.now();

    // GPU compute (WebGPU öncelikli)
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine && lines.length >= 4) {
      try {
        // Verileri flat array'e çevir
        const lineData = new Float32Array(lines.length * 6);
        for (let i = 0; i < lines.length; i++) {
          const base = i * 6;
          lineData[base] = lines[i].start.x;
          lineData[base + 1] = lines[i].start.y;
          lineData[base + 2] = lines[i].start.z;
          lineData[base + 3] = lines[i].end.x;
          lineData[base + 4] = lines[i].end.y;
          lineData[base + 5] = lines[i].end.z;
        }

        const result = await this.webGPUEngine.computeBatchOffsetDirections(
          lineData,
          { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          { x: camera.up.x, y: camera.up.y, z: camera.up.z }
        );

        // Float32Array'i Vector3 array'ine çevir
        const offsets: THREE.Vector3[] = [];
        for (let i = 0; i < lines.length; i++) {
          const base = i * 3;
          offsets.push(new THREE.Vector3(result[base], result[base + 1], result[base + 2]));
        }

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return offsets;
      } catch (e) {
        // GPU failed, fallback to CPU
      }
    }

    // CPU Fallback
    const offsets = this.cpuFallbackBatchOffset(lines, camera);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return offsets;
  }

  /**
   * 🚀 GPU Küre Tespiti - BufferGeometry için wrapper
   * @param geometry THREE.BufferGeometry
   * @returns {center, radius, quality} veya null
   */
  async detectSphere(geometry: THREE.BufferGeometry): Promise<{
    center: THREE.Vector3;
    radius: number;
    quality: number;
  } | null> {
    const startTime = performance.now();

    const positions = geometry.attributes.position.array as Float32Array;

    // WebGPU varsa GPU'da hesapla
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.detectSphere(positions);

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return result;
      } catch (e) {
        // GPU failed, fallback to CPU
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackDetectSphere(positions);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * 🚀 GPU Silindir Tespiti - BufferGeometry için wrapper
   * PCA (Principal Component Analysis) ile eksen bulma
   * @param geometry THREE.BufferGeometry
   * @returns {center, axis, radius, quality} veya null
   */
  async detectCylinder(geometry: THREE.BufferGeometry): Promise<{
    center: THREE.Vector3;
    axis: THREE.Vector3;
    radius: number;
    quality: number;
  } | null> {
    const startTime = performance.now();

    const positions = geometry.attributes.position.array as Float32Array;

    // WebGPU varsa GPU'da hesapla
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.detectCylinder(positions);

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return result;
      } catch (e) {
        // GPU failed, fallback to CPU
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackDetectCylinder(positions);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * CPU Fallback - Silindir tespiti (PCA ile)
   */
  private cpuFallbackDetectCylinder(positions: Float32Array): {
    center: THREE.Vector3;
    axis: THREE.Vector3;
    radius: number;
    quality: number;
  } | null {
    const vertexCount = positions.length / 3;
    if (vertexCount < 6) return null;

    // 1. Centroid hesapla
    let sumX = 0, sumY = 0, sumZ = 0;
    for (let i = 0; i < positions.length; i += 3) {
      sumX += positions[i];
      sumY += positions[i + 1];
      sumZ += positions[i + 2];
    }
    const center = new THREE.Vector3(sumX / vertexCount, sumY / vertexCount, sumZ / vertexCount);

    // 2. Covariance matrix hesapla (3x3)
    let cov00 = 0, cov01 = 0, cov02 = 0;
    let cov11 = 0, cov12 = 0, cov22 = 0;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - center.x;
      const dy = positions[i + 1] - center.y;
      const dz = positions[i + 2] - center.z;

      cov00 += dx * dx;
      cov01 += dx * dy;
      cov02 += dx * dz;
      cov11 += dy * dy;
      cov12 += dy * dz;
      cov22 += dz * dz;
    }

    // Normalize
    cov00 /= vertexCount;
    cov01 /= vertexCount;
    cov02 /= vertexCount;
    cov11 /= vertexCount;
    cov12 /= vertexCount;
    cov22 /= vertexCount;

    // 3. Power iteration - principal eigenvector (20 iterations)
    let vx = 1, vy = 0, vz = 0;
    for (let iter = 0; iter < 20; iter++) {
      const newVx = cov00 * vx + cov01 * vy + cov02 * vz;
      const newVy = cov01 * vx + cov11 * vy + cov12 * vz;
      const newVz = cov02 * vx + cov12 * vy + cov22 * vz;

      const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
      if (len > 0.0001) {
        vx = newVx / len;
        vy = newVy / len;
        vz = newVz / len;
      }
    }
    const axis = new THREE.Vector3(vx, vy, vz);

    // 4. Radial mesafeleri hesapla (eksenden dik mesafe)
    const radialDistances: number[] = [];
    let sumRadius = 0, minRadius = Infinity, maxRadius = -Infinity;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - center.x;
      const dy = positions[i + 1] - center.y;
      const dz = positions[i + 2] - center.z;

      // Eksene paralel bileşen
      const axialDist = dx * vx + dy * vy + dz * vz;

      // Eksene dik vektör
      const perpX = dx - axialDist * vx;
      const perpY = dy - axialDist * vy;
      const perpZ = dz - axialDist * vz;

      // Radyal mesafe
      const radialDist = Math.sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);
      radialDistances.push(radialDist);
      sumRadius += radialDist;
      minRadius = Math.min(minRadius, radialDist);
      maxRadius = Math.max(maxRadius, radialDist);
    }

    const avgRadius = sumRadius / vertexCount;

    // 5. Quality hesapla - radial mesafe tutarlılığı
    const tolerance = Math.max(0.1, avgRadius * 0.05);
    let inToleranceCount = 0;
    for (const dist of radialDistances) {
      if (Math.abs(dist - avgRadius) <= tolerance) {
        inToleranceCount++;
      }
    }
    const quality = (inToleranceCount / vertexCount) * 100;

    // Silindir olarak kabul edilebilir mi?
    // - Quality > 80%
    // - Min/max radius farkı avgRadius'un %30'undan az
    if (quality > 80 && (maxRadius - minRadius) < avgRadius * 0.3) {
      return { center, axis, radius: avgRadius, quality };
    }

    return null;
  }

  /**
   * CPU Fallback - Küre tespiti
   */
  private cpuFallbackDetectSphere(positions: Float32Array): {
    center: THREE.Vector3;
    radius: number;
    quality: number;
  } | null {
    const vertexCount = positions.length / 3;
    if (vertexCount < 4) return null;

    // Centroid hesapla
    let sumX = 0, sumY = 0, sumZ = 0;
    for (let i = 0; i < positions.length; i += 3) {
      sumX += positions[i];
      sumY += positions[i + 1];
      sumZ += positions[i + 2];
    }
    const center = new THREE.Vector3(sumX / vertexCount, sumY / vertexCount, sumZ / vertexCount);

    // Mesafeleri hesapla
    const distances: number[] = [];
    let sum = 0, min = Infinity, max = -Infinity;
    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - center.x;
      const dy = positions[i + 1] - center.y;
      const dz = positions[i + 2] - center.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      distances.push(dist);
      sum += dist;
      min = Math.min(min, dist);
      max = Math.max(max, dist);
    }
    const avgRadius = sum / vertexCount;

    // Quality hesapla
    const tolerance = Math.max(0.1, avgRadius * 0.05);
    let inToleranceCount = 0;
    for (const dist of distances) {
      if (Math.abs(dist - avgRadius) <= tolerance) {
        inToleranceCount++;
      }
    }
    const quality = (inToleranceCount / vertexCount) * 100;

    if (quality > 85 && (max - min) < avgRadius * 0.3) {
      return { center, radius: avgRadius, quality };
    }

    return null;
  }

  /**
   * CPU Fallback - Batch offset hesaplama
   */
  private cpuFallbackBatchOffset(
    lines: Array<{ start: THREE.Vector3; end: THREE.Vector3 }>,
    camera: THREE.Camera
  ): THREE.Vector3[] {
    const offsets: THREE.Vector3[] = [];
    const tempDirVec = new THREE.Vector3();
    const tempMidPoint = new THREE.Vector3();
    const tempCameraVec = new THREE.Vector3();
    const tempOffsetDir = new THREE.Vector3();

    for (const line of lines) {
      // Çizgi yönü
      tempDirVec.subVectors(line.end, line.start).normalize();

      // Orta nokta
      tempMidPoint.addVectors(line.start, line.end).multiplyScalar(0.5);

      // Kameradan orta noktaya
      tempCameraVec.subVectors(tempMidPoint, camera.position).normalize();

      // Cross product
      tempOffsetDir.crossVectors(tempDirVec, tempCameraVec);

      // Paralel durumu
      if (tempOffsetDir.lengthSq() < 0.01) {
        tempOffsetDir.crossVectors(tempDirVec, camera.up);
      }

      tempOffsetDir.normalize();

      // Kameraya doğru çevir
      if (tempOffsetDir.dot(tempCameraVec) < 0) {
        tempOffsetDir.negate();
      }

      // Y negatifse ters çevir
      if (tempOffsetDir.y < 0) {
        tempOffsetDir.negate();
      }

      offsets.push(tempOffsetDir.clone());
    }

    return offsets;
  }

  /**
   * CPU Fallback - WASM SIMD kullanılabilir ama şimdilik vanilla JS
   */
  private cpuFallbackMinDistance(pos1: Float32Array, pos2: Float32Array): number {
    // 🚀 ULTRA OPTIMIZED: Squared distance + early termination + sampling
    let minDistSq = Infinity;
    const EARLY_EXIT_THRESHOLD_SQ = 0.0001; // sqrt = 0.01mm - yeterince yakın

    const count1 = pos1.length / 3;
    const count2 = pos2.length / 3;

    // 🚀 SAMPLING: Çok büyük geometrilerde her vertex'i kontrol etme
    // 50K+ vertex varsa her 10'da birini kontrol et (10x hızlanma)
    const step1 = count1 > 50000 ? 10 : (count1 > 10000 ? 3 : 1);
    const step2 = count2 > 50000 ? 10 : (count2 > 10000 ? 3 : 1);

    for (let i = 0; i < pos1.length; i += 3 * step1) {
      const x1 = pos1[i];
      const y1 = pos1[i + 1];
      const z1 = pos1[i + 2];

      for (let j = 0; j < pos2.length; j += 3 * step2) {
        const dx = x1 - pos2[j];
        const dy = y1 - pos2[j + 1];
        const dz = z1 - pos2[j + 2];
        const distSq = dx * dx + dy * dy + dz * dz;

        if (distSq < minDistSq) {
          minDistSq = distSq;

          // 🚀 EARLY EXIT: Yeterince yakın bulunduysa dur
          if (minDistSq < EARLY_EXIT_THRESHOLD_SQ) {
            return Math.sqrt(minDistSq);
          }
        }
      }
    }

    return Math.sqrt(minDistSq);
  }

  /**
   * CPU Fallback - En yakın nokta çifti
   * 🚀 OPTIMIZED: Squared distance + early termination + sampling + refinement
   */
  private cpuFallbackClosestPoints(pos1: Float32Array, pos2: Float32Array): {
    mesafe: number;
    nokta1: THREE.Vector3;
    nokta2: THREE.Vector3;
  } {
    let minDistSq = Infinity;
    let minIdx1 = 0;
    let minIdx2 = 0;
    const EARLY_EXIT_THRESHOLD_SQ = 0.0001; // sqrt = 0.01mm

    const count1 = pos1.length / 3;
    const count2 = pos2.length / 3;

    // 🚀 PHASE 1: Coarse sampling to find approximate closest region
    const coarseStep1 = count1 > 10000 ? Math.max(1, Math.floor(count1 / 1000)) : 1;
    const coarseStep2 = count2 > 10000 ? Math.max(1, Math.floor(count2 / 1000)) : 1;

    for (let i = 0; i < pos1.length; i += 3 * coarseStep1) {
      const x1 = pos1[i];
      const y1 = pos1[i + 1];
      const z1 = pos1[i + 2];

      for (let j = 0; j < pos2.length; j += 3 * coarseStep2) {
        const dx = x1 - pos2[j];
        const dy = y1 - pos2[j + 1];
        const dz = z1 - pos2[j + 2];
        const distSq = dx * dx + dy * dy + dz * dz;

        if (distSq < minDistSq) {
          minDistSq = distSq;
          minIdx1 = i;
          minIdx2 = j;

          if (minDistSq < EARLY_EXIT_THRESHOLD_SQ) {
            return {
              mesafe: Math.sqrt(minDistSq),
              nokta1: new THREE.Vector3(pos1[minIdx1], pos1[minIdx1 + 1], pos1[minIdx1 + 2]),
              nokta2: new THREE.Vector3(pos2[minIdx2], pos2[minIdx2 + 1], pos2[minIdx2 + 2])
            };
          }
        }
      }
    }

    // 🚀 PHASE 2: Fine refinement around best candidate (if we did coarse sampling)
    if (coarseStep1 > 1 || coarseStep2 > 1) {
      const refineRadius = Math.max(coarseStep1, coarseStep2) * 2;
      const startI = Math.max(0, minIdx1 - refineRadius * 3);
      const endI = Math.min(pos1.length, minIdx1 + refineRadius * 3);
      const startJ = Math.max(0, minIdx2 - refineRadius * 3);
      const endJ = Math.min(pos2.length, minIdx2 + refineRadius * 3);

      for (let i = startI; i < endI; i += 3) {
        const x1 = pos1[i];
        const y1 = pos1[i + 1];
        const z1 = pos1[i + 2];

        for (let j = startJ; j < endJ; j += 3) {
          const dx = x1 - pos2[j];
          const dy = y1 - pos2[j + 1];
          const dz = z1 - pos2[j + 2];
          const distSq = dx * dx + dy * dy + dz * dz;

          if (distSq < minDistSq) {
            minDistSq = distSq;
            minIdx1 = i;
            minIdx2 = j;
          }
        }
      }
    }

    return {
      mesafe: Math.sqrt(minDistSq),
      nokta1: new THREE.Vector3(pos1[minIdx1], pos1[minIdx1 + 1], pos1[minIdx1 + 2]),
      nokta2: new THREE.Vector3(pos2[minIdx2], pos2[minIdx2 + 1], pos2[minIdx2 + 2])
    };
  }

  /**
   * CPU Fallback - Çizgiden düzleme mesafe
   */
  private cpuFallbackLineToPlane(
    linePos: Float32Array,
    planeNormal: THREE.Vector3,
    planePoint: THREE.Vector3
  ): { mesafe: number; noktaIndex: number; enYakinNokta: THREE.Vector3 } {
    let minDist = Infinity;
    let minIdx = 0;

    const nx = planeNormal.x;
    const ny = planeNormal.y;
    const nz = planeNormal.z;
    const px = planePoint.x;
    const py = planePoint.y;
    const pz = planePoint.z;

    for (let i = 0; i < linePos.length; i += 3) {
      const dx = linePos[i] - px;
      const dy = linePos[i + 1] - py;
      const dz = linePos[i + 2] - pz;
      const dist = Math.abs(dx * nx + dy * ny + dz * nz);

      if (dist < minDist) {
        minDist = dist;
        minIdx = i / 3;
      }
    }

    return {
      mesafe: minDist,
      noktaIndex: minIdx,
      enYakinNokta: new THREE.Vector3(linePos[minIdx * 3], linePos[minIdx * 3 + 1], linePos[minIdx * 3 + 2])
    };
  }

  /**
   * 🚀 GPU Koni Tespiti - BufferGeometry için wrapper
   * PCA (Principal Component Analysis) ile eksen bulma + Linear Regression ile açı hesaplama
   * @param geometry THREE.BufferGeometry
   * @returns {apex, axis, angle, quality} veya null
   */
  async detectCone(geometry: THREE.BufferGeometry): Promise<{
    apex: THREE.Vector3;
    axis: THREE.Vector3;
    angle: number; // Yarım açı (derece)
    quality: number;
  } | null> {
    const startTime = performance.now();

    const positions = geometry.attributes.position.array as Float32Array;

    // WebGPU varsa GPU'da hesapla
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.detectCone(positions);

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return result;
      } catch (e) {
        // GPU failed, fallback to CPU
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackDetectCone(positions);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * CPU Fallback - Koni tespiti (PCA + Linear Regression)
   */
  private cpuFallbackDetectCone(positions: Float32Array): {
    apex: THREE.Vector3;
    axis: THREE.Vector3;
    angle: number;
    quality: number;
  } | null {
    const vertexCount = positions.length / 3;
    if (vertexCount < 10) return null;

    // 1. Centroid hesapla
    let sumX = 0, sumY = 0, sumZ = 0;
    for (let i = 0; i < positions.length; i += 3) {
      sumX += positions[i];
      sumY += positions[i + 1];
      sumZ += positions[i + 2];
    }
    const centroidX = sumX / vertexCount;
    const centroidY = sumY / vertexCount;
    const centroidZ = sumZ / vertexCount;

    // 2. Covariance matrix hesapla (3x3)
    let cov00 = 0, cov01 = 0, cov02 = 0;
    let cov11 = 0, cov12 = 0, cov22 = 0;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - centroidX;
      const dy = positions[i + 1] - centroidY;
      const dz = positions[i + 2] - centroidZ;

      cov00 += dx * dx;
      cov01 += dx * dy;
      cov02 += dx * dz;
      cov11 += dy * dy;
      cov12 += dy * dz;
      cov22 += dz * dz;
    }

    // Normalize
    cov00 /= vertexCount;
    cov01 /= vertexCount;
    cov02 /= vertexCount;
    cov11 /= vertexCount;
    cov12 /= vertexCount;
    cov22 /= vertexCount;

    // 3. Power iteration - principal eigenvector (20 iterations)
    let vx = 1, vy = 0, vz = 0;
    for (let iter = 0; iter < 20; iter++) {
      const newVx = cov00 * vx + cov01 * vy + cov02 * vz;
      const newVy = cov01 * vx + cov11 * vy + cov12 * vz;
      const newVz = cov02 * vx + cov12 * vy + cov22 * vz;

      const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
      if (len > 0.0001) {
        vx = newVx / len;
        vy = newVy / len;
        vz = newVz / len;
      }
    }

    // 4. Her nokta için axial position (h) ve radial distance (r) hesapla
    const hValues: number[] = [];
    const rValues: number[] = [];

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - centroidX;
      const dy = positions[i + 1] - centroidY;
      const dz = positions[i + 2] - centroidZ;

      // Eksene paralel bileşen (axial position)
      const h = dx * vx + dy * vy + dz * vz;

      // Eksene dik vektör
      const perpX = dx - h * vx;
      const perpY = dy - h * vy;
      const perpZ = dz - h * vz;

      // Radyal mesafe
      const r = Math.sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);

      hValues.push(h);
      rValues.push(r);
    }

    // 5. Linear Regression: r = a * h + b
    let sumH = 0, sumR = 0, sumHH = 0, sumHR = 0;
    for (let i = 0; i < vertexCount; i++) {
      sumH += hValues[i];
      sumR += rValues[i];
      sumHH += hValues[i] * hValues[i];
      sumHR += hValues[i] * rValues[i];
    }

    const meanH = sumH / vertexCount;
    const meanR = sumR / vertexCount;

    const denominator = sumHH - vertexCount * meanH * meanH;
    if (Math.abs(denominator) < 0.0001) return null;

    const slope = (sumHR - vertexCount * meanH * meanR) / denominator;
    const intercept = meanR - slope * meanH;

    // Koni açısı (yarım açı) = atan(slope)
    const halfAngleRad = Math.atan(Math.abs(slope));
    const halfAngleDeg = halfAngleRad * (180 / Math.PI);

    // Koni mi silindir mi? slope ~0 ise silindir
    if (Math.abs(slope) < 0.05) return null; // Silindir, koni değil

    // 6. Apex noktası hesapla: r = 0 olduğunda h = -b/a
    const hApex = -intercept / slope;
    const apexX = centroidX + hApex * vx;
    const apexY = centroidY + hApex * vy;
    const apexZ = centroidZ + hApex * vz;

    // 7. Quality hesapla - regression fit quality (R²)
    let ssTot = 0, ssRes = 0;
    for (let i = 0; i < vertexCount; i++) {
      const rPredicted = slope * hValues[i] + intercept;
      ssTot += (rValues[i] - meanR) * (rValues[i] - meanR);
      ssRes += (rValues[i] - rPredicted) * (rValues[i] - rPredicted);
    }

    const rSquared = ssTot > 0.0001 ? 1 - (ssRes / ssTot) : 0;
    const quality = rSquared * 100;

    // Koni olarak kabul edilebilir mi?
    // - Quality > 75%
    // - Açı 5-85 derece arası (çok dar veya çok geniş değil)
    if (quality > 75 && halfAngleDeg > 5 && halfAngleDeg < 85) {
      return {
        apex: new THREE.Vector3(apexX, apexY, apexZ),
        axis: new THREE.Vector3(vx, vy, vz),
        angle: halfAngleDeg,
        quality
      };
    }

    return null;
  }

  /**
   * 🚀 GPU Torus (Halka) Tespiti - BufferGeometry için wrapper
   * Major radius (R) ve minor radius (r) hesaplama
   * @param geometry THREE.BufferGeometry
   * @returns {center, axis, majorRadius, minorRadius, quality} veya null
   */
  async detectTorus(geometry: THREE.BufferGeometry): Promise<{
    center: THREE.Vector3;
    axis: THREE.Vector3;
    majorRadius: number; // Merkeze olan mesafe (R)
    minorRadius: number; // Tüp yarıçapı (r)
    quality: number;
  } | null> {
    const startTime = performance.now();

    const positions = geometry.attributes.position.array as Float32Array;

    // WebGPU varsa GPU'da hesapla
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.detectTorus(positions);

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return result;
      } catch (e) {
        // GPU failed, fallback to CPU
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackDetectTorus(positions);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * CPU Fallback - Torus tespiti
   * Torus denklemi: (sqrt(x² + y²) - R)² + z² = r²
   */
  private cpuFallbackDetectTorus(positions: Float32Array): {
    center: THREE.Vector3;
    axis: THREE.Vector3;
    majorRadius: number;
    minorRadius: number;
    quality: number;
  } | null {
    const vertexCount = positions.length / 3;
    if (vertexCount < 20) return null;

    // 1. Centroid hesapla
    let sumX = 0, sumY = 0, sumZ = 0;
    for (let i = 0; i < positions.length; i += 3) {
      sumX += positions[i];
      sumY += positions[i + 1];
      sumZ += positions[i + 2];
    }
    const centroidX = sumX / vertexCount;
    const centroidY = sumY / vertexCount;
    const centroidZ = sumZ / vertexCount;

    // 2. Covariance matrix hesapla - normal ekseni bulmak için
    let cov00 = 0, cov01 = 0, cov02 = 0;
    let cov11 = 0, cov12 = 0, cov22 = 0;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - centroidX;
      const dy = positions[i + 1] - centroidY;
      const dz = positions[i + 2] - centroidZ;

      cov00 += dx * dx;
      cov01 += dx * dy;
      cov02 += dx * dz;
      cov11 += dy * dy;
      cov12 += dy * dz;
      cov22 += dz * dz;
    }

    cov00 /= vertexCount;
    cov01 /= vertexCount;
    cov02 /= vertexCount;
    cov11 /= vertexCount;
    cov12 /= vertexCount;
    cov22 /= vertexCount;

    // 3. Power iteration - en küçük eigenvector (normal eksen)
    // Torus için normal, en az varyansa sahip yön
    // İlk olarak en büyük eigenvector'u bulalım, sonra deflation uygulayalım
    let vx = 1, vy = 0, vz = 0;
    for (let iter = 0; iter < 20; iter++) {
      const newVx = cov00 * vx + cov01 * vy + cov02 * vz;
      const newVy = cov01 * vx + cov11 * vy + cov12 * vz;
      const newVz = cov02 * vx + cov12 * vy + cov22 * vz;

      const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
      if (len > 0.0001) {
        vx = newVx / len;
        vy = newVy / len;
        vz = newVz / len;
      }
    }

    // İkinci eigenvector için deflation
    const lambda1 = cov00 * vx * vx + 2 * cov01 * vx * vy + 2 * cov02 * vx * vz +
                    cov11 * vy * vy + 2 * cov12 * vy * vz + cov22 * vz * vz;

    // Deflated matrix
    const d00 = cov00 - lambda1 * vx * vx;
    const d01 = cov01 - lambda1 * vx * vy;
    const d02 = cov02 - lambda1 * vx * vz;
    const d11 = cov11 - lambda1 * vy * vy;
    const d12 = cov12 - lambda1 * vy * vz;
    const d22 = cov22 - lambda1 * vz * vz;

    // İkinci eigenvector
    let v2x = 0, v2y = 1, v2z = 0;
    for (let iter = 0; iter < 20; iter++) {
      const newVx = d00 * v2x + d01 * v2y + d02 * v2z;
      const newVy = d01 * v2x + d11 * v2y + d12 * v2z;
      const newVz = d02 * v2x + d12 * v2y + d22 * v2z;

      const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
      if (len > 0.0001) {
        v2x = newVx / len;
        v2y = newVy / len;
        v2z = newVz / len;
      }
    }

    // Üçüncü eigenvector (cross product) - en küçük varyans yönü (normal)
    const normalX = vy * v2z - vz * v2y;
    const normalY = vz * v2x - vx * v2z;
    const normalZ = vx * v2y - vy * v2x;
    const normalLen = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);

    const axisX = normalX / normalLen;
    const axisY = normalY / normalLen;
    const axisZ = normalZ / normalLen;

    // 4. Major radius hesapla - noktaların eksenden 2D mesafesi
    const planarDistances: number[] = [];
    let sumPlanarDist = 0;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - centroidX;
      const dy = positions[i + 1] - centroidY;
      const dz = positions[i + 2] - centroidZ;

      // Eksene dik düzleme projeksiyon
      const axialDist = dx * axisX + dy * axisY + dz * axisZ;
      const perpX = dx - axialDist * axisX;
      const perpY = dy - axialDist * axisY;
      const perpZ = dz - axialDist * axisZ;

      const planarDist = Math.sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);
      planarDistances.push(planarDist);
      sumPlanarDist += planarDist;
    }

    const avgPlanarDist = sumPlanarDist / vertexCount;

    // 5. Minor radius hesapla - torus tüp yarıçapı
    // Her nokta için: distance_to_tube_center = |sqrt(x² + y²) - R|
    // z² + distance_to_tube_center² = r²
    const tubeDistances: number[] = [];
    let sumTubeDist = 0;

    for (let i = 0; i < vertexCount; i++) {
      const dx = positions[i * 3] - centroidX;
      const dy = positions[i * 3 + 1] - centroidY;
      const dz = positions[i * 3 + 2] - centroidZ;

      // Eksene olan mesafe
      const axialDist = dx * axisX + dy * axisY + dz * axisZ;

      // Planar düzlemdeki radyal mesafe
      const planarRadial = planarDistances[i];

      // Tüp merkezine olan mesafe
      const toTubeCenter = Math.abs(planarRadial - avgPlanarDist);
      const tubeDist = Math.sqrt(toTubeCenter * toTubeCenter + axialDist * axialDist);

      tubeDistances.push(tubeDist);
      sumTubeDist += tubeDist;
    }

    const avgTubeDist = sumTubeDist / vertexCount;

    // 6. Quality hesapla - tüp mesafesi tutarlılığı
    const tolerance = Math.max(0.1, avgTubeDist * 0.1);
    let inToleranceCount = 0;
    for (const dist of tubeDistances) {
      if (Math.abs(dist - avgTubeDist) <= tolerance) {
        inToleranceCount++;
      }
    }
    const quality = (inToleranceCount / vertexCount) * 100;

    // Torus olarak kabul edilebilir mi?
    // - Quality > 70%
    // - Major radius > minor radius * 1.5 (geçerli torus oranı)
    // - Minor radius > 0
    if (quality > 70 && avgPlanarDist > avgTubeDist * 1.5 && avgTubeDist > 0.01) {
      return {
        center: new THREE.Vector3(centroidX, centroidY, centroidZ),
        axis: new THREE.Vector3(axisX, axisY, axisZ),
        majorRadius: avgPlanarDist,
        minorRadius: avgTubeDist,
        quality
      };
    }

    return null;
  }

  /**
   * 🚀 GPU Düzlem Tespiti - RANSAC ile robust plane fitting
   * @param geometry THREE.BufferGeometry
   * @returns {point, normal, quality} veya null
   */
  async detectPlane(geometry: THREE.BufferGeometry): Promise<{
    point: THREE.Vector3;
    normal: THREE.Vector3;
    quality: number;
  } | null> {
    const startTime = performance.now();

    const positions = geometry.attributes.position.array as Float32Array;

    // WebGPU varsa GPU'da hesapla
    if (this.accelerationMode === 'webgpu' && this.webGPUEngine) {
      try {
        const result = await this.webGPUEngine.detectPlane(positions);

        const elapsed = performance.now() - startTime;
        this.metrics.computeCount++;
        this.metrics.computeTotalTime += elapsed;

        return result;
      } catch (e) {
        // GPU failed, fallback to CPU
      }
    }

    // CPU Fallback
    const cpuResult = this.cpuFallbackDetectPlane(positions);

    const elapsed = performance.now() - startTime;
    this.metrics.computeCount++;
    this.metrics.computeTotalTime += elapsed;

    return cpuResult;
  }

  /**
   * CPU Fallback - Düzlem tespiti (PCA + RANSAC-lite)
   */
  private cpuFallbackDetectPlane(positions: Float32Array): {
    point: THREE.Vector3;
    normal: THREE.Vector3;
    quality: number;
  } | null {
    const vertexCount = positions.length / 3;
    if (vertexCount < 3) return null;

    // 1. Centroid hesapla
    let sumX = 0, sumY = 0, sumZ = 0;
    for (let i = 0; i < positions.length; i += 3) {
      sumX += positions[i];
      sumY += positions[i + 1];
      sumZ += positions[i + 2];
    }
    const centroidX = sumX / vertexCount;
    const centroidY = sumY / vertexCount;
    const centroidZ = sumZ / vertexCount;

    // 2. Covariance matrix hesapla
    let cov00 = 0, cov01 = 0, cov02 = 0;
    let cov11 = 0, cov12 = 0, cov22 = 0;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - centroidX;
      const dy = positions[i + 1] - centroidY;
      const dz = positions[i + 2] - centroidZ;

      cov00 += dx * dx;
      cov01 += dx * dy;
      cov02 += dx * dz;
      cov11 += dy * dy;
      cov12 += dy * dz;
      cov22 += dz * dz;
    }

    cov00 /= vertexCount;
    cov01 /= vertexCount;
    cov02 /= vertexCount;
    cov11 /= vertexCount;
    cov12 /= vertexCount;
    cov22 /= vertexCount;

    // 3. En büyük iki eigenvector'ü bul
    let v1x = 1, v1y = 0, v1z = 0;
    for (let iter = 0; iter < 20; iter++) {
      const newVx = cov00 * v1x + cov01 * v1y + cov02 * v1z;
      const newVy = cov01 * v1x + cov11 * v1y + cov12 * v1z;
      const newVz = cov02 * v1x + cov12 * v1y + cov22 * v1z;

      const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
      if (len > 0.0001) {
        v1x = newVx / len;
        v1y = newVy / len;
        v1z = newVz / len;
      }
    }

    // Deflation
    const lambda1 = cov00 * v1x * v1x + 2 * cov01 * v1x * v1y + 2 * cov02 * v1x * v1z +
                    cov11 * v1y * v1y + 2 * cov12 * v1y * v1z + cov22 * v1z * v1z;

    const d00 = cov00 - lambda1 * v1x * v1x;
    const d01 = cov01 - lambda1 * v1x * v1y;
    const d02 = cov02 - lambda1 * v1x * v1z;
    const d11 = cov11 - lambda1 * v1y * v1y;
    const d12 = cov12 - lambda1 * v1y * v1z;
    const d22 = cov22 - lambda1 * v1z * v1z;

    let v2x = 0, v2y = 1, v2z = 0;
    for (let iter = 0; iter < 20; iter++) {
      const newVx = d00 * v2x + d01 * v2y + d02 * v2z;
      const newVy = d01 * v2x + d11 * v2y + d12 * v2z;
      const newVz = d02 * v2x + d12 * v2y + d22 * v2z;

      const len = Math.sqrt(newVx * newVx + newVy * newVy + newVz * newVz);
      if (len > 0.0001) {
        v2x = newVx / len;
        v2y = newVy / len;
        v2z = newVz / len;
      }
    }

    // 4. Normal = cross product (en küçük varyans yönü)
    let normalX = v1y * v2z - v1z * v2y;
    let normalY = v1z * v2x - v1x * v2z;
    let normalZ = v1x * v2y - v1y * v2x;
    const normalLen = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);

    if (normalLen < 0.0001) return null;

    normalX /= normalLen;
    normalY /= normalLen;
    normalZ /= normalLen;

    // 5. Quality hesapla - düzleme olan mesafelerin tutarlılığı
    const distances: number[] = [];
    let sumDist = 0;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - centroidX;
      const dy = positions[i + 1] - centroidY;
      const dz = positions[i + 2] - centroidZ;

      const dist = Math.abs(dx * normalX + dy * normalY + dz * normalZ);
      distances.push(dist);
      sumDist += dist;
    }

    const avgDist = sumDist / vertexCount;

    // Düzlem için tolerans çok düşük olmalı
    const tolerance = Math.max(0.01, avgDist * 2);
    let inToleranceCount = 0;
    for (const dist of distances) {
      if (dist <= tolerance) {
        inToleranceCount++;
      }
    }
    const quality = (inToleranceCount / vertexCount) * 100;

    // Düzlem olarak kabul edilebilir mi?
    // - Quality > 90% (düzlem çok hassas olmalı)
    // - Ortalama mesafe çok küçük olmalı
    if (quality > 90) {
      return {
        point: new THREE.Vector3(centroidX, centroidY, centroidZ),
        normal: new THREE.Vector3(normalX, normalY, normalZ),
        quality
      };
    }

    return null;
  }

  /**
   * Performans istatistikleri
   */
  getPerformanceStats(): {
    mode: string;
    avgRaycastTime: number;
    avgComputeTime: number;
    totalRaycasts: number;
    totalComputes: number;
  } {
    return {
      mode: this.accelerationMode.toUpperCase(),
      avgRaycastTime: this.metrics.raycastCount > 0
        ? this.metrics.raycastTotalTime / this.metrics.raycastCount
        : 0,
      avgComputeTime: this.metrics.computeCount > 0
        ? this.metrics.computeTotalTime / this.metrics.computeCount
        : 0,
      totalRaycasts: this.metrics.raycastCount,
      totalComputes: this.metrics.computeCount,
    };
  }

  /**
   * Motor'u temizle
   */
  dispose(): void {
    this.webGPUEngine?.dispose();
    this.webGL2Engine?.dispose();
    this.webGPUEngine = null;
    this.webGL2Engine = null;
    this.initialized = false;
  }
}

// Singleton instance - globally accessible
export const gpuOlcumMotoru = new GPUOlcumMotoru();
